import { useState, useEffect, useRef, useCallback } from "react";

// ══════════════════════════════════════════════════════════════
//  GAME CONSTANTS
// ══════════════════════════════════════════════════════════════
const levelThreshold = (lvl) => Math.round(100 * Math.pow(lvl, 1.35));

// Progress milestones within a level (% of XP)
// 25% → visual effect   50% → crit-click chance  75% → temp x2 boost  100% → level up
const MILESTONES = [
  { pct: 25, id: "visual",  label: "✨ Визуальный эффект",   desc: "Голубь светится!" },
  { pct: 50, id: "crit",    label: "⚡ Крит-клик",           desc: "+10% шанс крита x3" },
  { pct: 75, id: "boost",   label: "🔥 Двойной буст",        desc: "x2 на 30 секунд" },
  { pct: 100, id: "levelup", label: "🏆 Уровень!",           desc: "" },
];

// Talent tree definitions
const TALENT_TREES = {
  active: {
    name: "Активный игрок",
    icon: "👆",
    color: "#FF8C00",
    talents: [
      { id: "click_plus1",    name: "+1 к клику",         desc: "Каждый клик даёт +1 дополнительное очко",   cost: 1, max: 5 },
      { id: "combo_pct",      name: "+10% к комбо",       desc: "Комбо множитель растёт быстрее",            cost: 1, max: 3 },
      { id: "crit_chance",    name: "+2% шанс крита",     desc: "Больше критических кликов",                 cost: 1, max: 5 },
    ]
  },
  passive: {
    name: "Пассивный доход",
    icon: "💰",
    color: "#00BFFF",
    talents: [
      { id: "passive_inc",    name: "+1 очко/сек",        desc: "Автоматически зарабатываешь очки",          cost: 1, max: 10 },
      { id: "gen_boost",      name: "+10% генераторы",    desc: "Все пассивные источники работают лучше",    cost: 1, max: 3 },
      { id: "gen_cheap",      name: "Генераторы -5%",     desc: "Пассивный доход дешевле в магазине",        cost: 1, max: 3 },
    ]
  },
  rare: {
    name: "Редкие бусты",
    icon: "🎲",
    color: "#CC44FF",
    talents: [
      { id: "boost_power",    name: "Сильнее бусты",      desc: "Временные бонусы дают +50% эффект",        cost: 1, max: 3 },
      { id: "event_chance",   name: "Больше событий",     desc: "Случайные события случаются чаще",          cost: 1, max: 3 },
      { id: "coin_bonus",     name: "Монеты ×2",          desc: "Золотые монеты дают вдвое больше очков",   cost: 1, max: 3 },
    ]
  }
};

// ══════════════════════════════════════════════════════════════
//  PIGEON SKINS (all with 2 eyes)
// ══════════════════════════════════════════════════════════════
const SKINS = [
  { id:"default",  name:"Обычный Голубь",    price:0,    emoji:"🐦", color:"#7B9BC8",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="#7B9BC8"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#8BAAD8"/>
      <circle cx="60" cy="42" r="14" fill="#9BBAE8"/>
      <circle cx="54" cy="38" r="4" fill="white"/><circle cx="54" cy="38" r="2.2" fill="#222"/><circle cx="54.8" cy="37.2" r="0.8" fill="white"/>
      <circle cx="66" cy="38" r="4" fill="white"/><circle cx="66" cy="38" r="2.2" fill="#222"/><circle cx="66.8" cy="37.2" r="0.8" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#F4A225"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#6A8AB8" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#6A8AB8" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#F4A225" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#F4A225" rx="2"/>
    </svg>)
  },
  { id:"royal",   name:"Королевский", price:2500,  emoji:"👑", color:"#FFD700",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="#C8A830"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#D8B840"/>
      <circle cx="60" cy="42" r="14" fill="#E8C850"/>
      <circle cx="54" cy="38" r="4" fill="white"/><circle cx="54" cy="38" r="2.2" fill="#222"/><circle cx="54.8" cy="37.2" r="0.8" fill="white"/>
      <circle cx="66" cy="38" r="4" fill="white"/><circle cx="66" cy="38" r="2.2" fill="#222"/><circle cx="66.8" cy="37.2" r="0.8" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#FF6600"/>
      <polygon points="52,30 55,22 58,30 61,18 64,30 67,22 70,30" fill="#FFD700" stroke="#FFA000" strokeWidth="1"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#B89820" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#B89820" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#FF8800" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#FF8800" rx="2"/>
    </svg>)
  },
  { id:"ninja", name:"Ниндзя", price:5000, emoji:"🥷", color:"#2C2C2C",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="#1a1a1a"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#222"/>
      <circle cx="60" cy="42" r="14" fill="#2C2C2C"/>
      <rect x="46" y="40" width="28" height="7" fill="#111" rx="3"/>
      <circle cx="54" cy="37" r="3.2" fill="#FF3030"/><circle cx="54" cy="37" r="1.8" fill="#800000"/><circle cx="54.7" cy="36.3" r="0.6" fill="#FF8080"/>
      <circle cx="66" cy="37" r="3.2" fill="#FF3030"/><circle cx="66" cy="37" r="1.8" fill="#800000"/><circle cx="66.7" cy="36.3" r="0.6" fill="#FF8080"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#111" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#111" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#333" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#333" rx="2"/>
      <line x1="72" y1="60" x2="90" y2="45" stroke="#888" strokeWidth="2"/>
      <polygon points="90,40 95,45 88,48" fill="#ccc"/>
    </svg>)
  },
  { id:"cyber", name:"Кибер", price:18000, emoji:"🤖", color:"#00FFFF",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="#1a3a4a"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#1E4060"/>
      <circle cx="60" cy="42" r="14" fill="#204458"/>
      <rect x="46" y="36" width="28" height="4" fill="#00FFFF" opacity="0.3" rx="2"/>
      <circle cx="54" cy="38" r="4" fill="#001020"/><circle cx="54" cy="38" r="3" fill="#00FFFF" opacity="0.9"/><circle cx="54.8" cy="37.2" r="1.2" fill="white"/>
      <circle cx="66" cy="38" r="4" fill="#001020"/><circle cx="66" cy="38" r="3" fill="#00FFFF" opacity="0.9"/><circle cx="66.8" cy="37.2" r="1.2" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#00FFFF"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#153040" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#153040" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#00FFFF" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#00FFFF" rx="2"/>
    </svg>)
  },
  { id:"fire", name:"Огненный", price:28000, emoji:"🔥", color:"#FF4500",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <defs><linearGradient id="fire1" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FF2200"/><stop offset="50%" stopColor="#FF8800"/><stop offset="100%" stopColor="#FFCC00"/></linearGradient></defs>
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="url(#fire1)"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#FF6600"/>
      <circle cx="60" cy="42" r="14" fill="#FF8800"/>
      <circle cx="54" cy="38" r="4" fill="#FFE0A0"/><circle cx="54" cy="38" r="2.2" fill="#400"/><circle cx="54.8" cy="37.2" r="0.8" fill="#FFAA44"/>
      <circle cx="66" cy="38" r="4" fill="#FFE0A0"/><circle cx="66" cy="38" r="2.2" fill="#400"/><circle cx="66.8" cy="37.2" r="0.8" fill="#FFAA44"/>
      <polygon points="59,44 67,46 59,49" fill="#FFD700"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#CC3300" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#CC3300" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#FF6600" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#FF6600" rx="2"/>
      <path d="M55 28 Q58 20 60 15 Q62 20 65 18 Q63 25 66 28 Q62 24 60 30 Q58 24 55 28Z" fill="#FFEE44"/>
    </svg>)
  },
  { id:"diamond", name:"Бриллиантовый", price:75000, emoji:"💎", color:"#B9F2FF",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <defs><linearGradient id="diam" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#B9F2FF"/><stop offset="50%" stopColor="#7FD8FF"/><stop offset="100%" stopColor="#00BFFF"/></linearGradient></defs>
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="url(#diam)" opacity="0.95"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#A8EAFF"/>
      <circle cx="60" cy="42" r="14" fill="#C8F4FF"/>
      <circle cx="54" cy="38" r="4" fill="white"/><circle cx="54" cy="38" r="2.2" fill="#004466"/><circle cx="54.8" cy="37.2" r="1" fill="white"/>
      <circle cx="66" cy="38" r="4" fill="white"/><circle cx="66" cy="38" r="2.2" fill="#004466"/><circle cx="66.8" cy="37.2" r="1" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#0099CC"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#88D8F8" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#88D8F8" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#0099CC" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#0099CC" rx="2"/>
      <polygon points="60,22 66,30 60,34 54,30" fill="white" opacity="0.8"/>
    </svg>)
  },
  { id:"zombie", name:"Зомби", price:10000, emoji:"🧟", color:"#6B8E23",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="#4A6B1A"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#5A7A22"/>
      <circle cx="60" cy="42" r="14" fill="#6B8E23"/>
      <circle cx="54" cy="38" r="4" fill="#CCFF44"/><circle cx="54" cy="38" r="2.2" fill="#446600"/><circle cx="54.8" cy="37.2" r="0.8" fill="#AAFFAA"/>
      <circle cx="66" cy="37" r="4.5" fill="#FF4444"/><circle cx="66" cy="37" r="2.5" fill="#880000"/><circle cx="66.8" cy="36.2" r="0.8" fill="#FFAAAA"/>
      <line x1="46" y1="43" x2="74" y2="44" stroke="#385010" strokeWidth="1.5"/>
      <polygon points="59,45 67,47 59,50" fill="#8B6914"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#3A5A10" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#3A5A10" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#556B2F" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#556B2F" rx="2"/>
    </svg>)
  },
  { id:"space", name:"Космический", price:45000, emoji:"🚀", color:"#6A0DAD",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <defs><radialGradient id="space1" cx="50%" cy="50%"><stop offset="0%" stopColor="#9B30FF"/><stop offset="100%" stopColor="#3D0080"/></radialGradient></defs>
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="url(#space1)"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#7722CC"/>
      <circle cx="60" cy="42" r="14" fill="#8833DD"/>
      <rect x="56" y="26" width="8" height="10" fill="#aaffee" rx="3" opacity="0.8"/>
      <circle cx="54" cy="38" r="4.5" fill="#001a3a"/><circle cx="54" cy="38" r="3" fill="#FFFFFF"/><circle cx="54" cy="38" r="1.8" fill="#00FFCC"/><circle cx="54.7" cy="37.3" r="0.7" fill="white"/>
      <circle cx="66" cy="38" r="4.5" fill="#001a3a"/><circle cx="66" cy="38" r="3" fill="#FFFFFF"/><circle cx="66" cy="38" r="1.8" fill="#00FFCC"/><circle cx="66.7" cy="37.3" r="0.7" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#CC88FF"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#5511AA" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#5511AA" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#AA44FF" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#AA44FF" rx="2"/>
      <circle cx="44" cy="32" r="1.5" fill="white"/><circle cx="78" cy="28" r="1" fill="white"/>
    </svg>)
  },
  { id:"ghost", name:"Призрак", price:8000, emoji:"👻", color:"#E8E8FF",
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="white" opacity="0.7"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#F0F0FF" opacity="0.8"/>
      <circle cx="60" cy="42" r="14" fill="white" opacity="0.9"/>
      <circle cx="54" cy="38" r="4.5" fill="white"/><circle cx="54" cy="38" r="3.2" fill="#9090FF"/><circle cx="54" cy="38" r="1.8" fill="#333"/><circle cx="54.7" cy="37.3" r="0.7" fill="white"/>
      <circle cx="66" cy="38" r="4.5" fill="white"/><circle cx="66" cy="38" r="3.2" fill="#9090FF"/><circle cx="66" cy="38" r="1.8" fill="#333"/><circle cx="66.7" cy="37.3" r="0.7" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#B0B0FF"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#E8E8FF" opacity="0.6" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#E8E8FF" opacity="0.6" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#C0C0FF" opacity="0.8" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#C0C0FF" opacity="0.8" rx="2"/>
    </svg>)
  },
  { id:"rainbow", name:"Радужный", price:6500,  emoji:"🌈", color:"#FF69B4",    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <defs>
        <linearGradient id="rb1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FF6B6B"/><stop offset="50%" stopColor="#4ECDC4"/><stop offset="100%" stopColor="#96CEB4"/></linearGradient>
        <linearGradient id="rb2" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FFEAA7"/><stop offset="50%" stopColor="#DDA0DD"/><stop offset="100%" stopColor="#98D8C8"/></linearGradient>
      </defs>
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="url(#rb1)"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="url(#rb2)"/>
      <circle cx="60" cy="42" r="14" fill="url(#rb1)"/>
      <circle cx="54" cy="38" r="4" fill="white"/><circle cx="54" cy="38" r="2.2" fill="#7700AA"/><circle cx="54.8" cy="37.2" r="0.8" fill="white"/>
      <circle cx="66" cy="38" r="4" fill="white"/><circle cx="66" cy="38" r="2.2" fill="#7700AA"/><circle cx="66.8" cy="37.2" r="0.8" fill="white"/>
      <polygon points="59,44 67,46 59,49" fill="#FF6600"/>
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="url(#rb2)" transform="rotate(-20 38 78)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="url(#rb2)" transform="rotate(20 82 78)"/>
      <rect x="49" y="92" width="4" height="8" fill="#FF8800" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#FF8800" rx="2"/>
    </svg>)
  },
  // ★ DEV PIGEON — developer gift, admin-only. Perma x10 boost + frequent juniors.
  { id:"capybara", name:"Dev Голубь 👨‍💻", price:0, emoji:"💻", color:"#00FFCC", isDeveloperGift:true,
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <defs>
        <radialGradient id="dev_body" cx="40%" cy="35%"><stop offset="0%" stopColor="#B0FFF0"/><stop offset="55%" stopColor="#00C8A0"/><stop offset="100%" stopColor="#007860"/></radialGradient>
        <radialGradient id="dev_head" cx="45%" cy="35%"><stop offset="0%" stopColor="#C8FFF4"/><stop offset="60%" stopColor="#00D4A8"/><stop offset="100%" stopColor="#008870"/></radialGradient>
        <filter id="dev_glow"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      {/* glow aura */}
      <ellipse cx="60" cy="68" rx="42" ry="32" fill="#00FFCC" opacity="0.10" filter="url(#dev_glow)"/>
      {/* body */}
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="url(#dev_body)" filter="url(#dev_glow)"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#00D4A8"/>
      {/* head */}
      <circle cx="60" cy="42" r="14" fill="url(#dev_head)" filter="url(#dev_glow)"/>
      {/* laptop screen on chest */}
      <rect x="44" y="67" width="32" height="20" rx="3" fill="#0A0A1A"/>
      <rect x="46" y="69" width="28" height="16" rx="2" fill="#001A10"/>
      {/* code lines on laptop */}
      <rect x="48" y="71" width="14" height="2" rx="1" fill="#00FF88" opacity="0.9"/>
      <rect x="48" y="75" width="20" height="2" rx="1" fill="#00CCFF" opacity="0.8"/>
      <rect x="48" y="79" width="10" height="2" rx="1" fill="#FF6680" opacity="0.8"/>
      <rect x="60" y="79" width="8" height="2" rx="1" fill="#00FF88" opacity="0.7"/>
      <rect x="48" y="83" width="16" height="2" rx="1" fill="#FFD700" opacity="0.7"/>
      {/* laptop hinge/base */}
      <rect x="42" y="87" width="36" height="3" rx="1.5" fill="#1A1A2E"/>
      {/* eyes — green terminal style */}
      <circle cx="54" cy="38" r="4.5" fill="#001810"/>
      <circle cx="54" cy="38" r="3"   fill="#00FF88" opacity="0.9"/>
      <circle cx="54" cy="38" r="1.5" fill="#AAFFDD"/>
      <circle cx="54.8" cy="37.2" r="0.7" fill="white"/>
      <circle cx="66" cy="38" r="4.5" fill="#001810"/>
      <circle cx="66" cy="38" r="3"   fill="#00FF88" opacity="0.9"/>
      <circle cx="66" cy="38" r="1.5" fill="#AAFFDD"/>
      <circle cx="66.8" cy="37.2" r="0.7" fill="white"/>
      {/* beak */}
      <polygon points="58,44 68,46 58,49" fill="#FFD700"/>
      {/* headphones */}
      <path d="M46 36 Q44 26 60 24 Q76 26 74 36" stroke="#333" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <rect x="42" y="34" width="7" height="7" rx="3" fill="#222"/>
      <rect x="71" y="34" width="7" height="7" rx="3" fill="#222"/>
      <rect x="43" y="35" width="5" height="5" rx="2.5" fill="#00FFCC" opacity="0.7"/>
      <rect x="72" y="35" width="5" height="5" rx="2.5" fill="#00FFCC" opacity="0.7"/>
      {/* wings */}
      <ellipse cx="36" cy="76" rx="14" ry="8" fill="#00A880" transform="rotate(-20 36 76)" filter="url(#dev_glow)"/>
      <ellipse cx="84" cy="76" rx="14" ry="8" fill="#00A880" transform="rotate(20 84 76)" filter="url(#dev_glow)"/>
      {/* feet */}
      <rect x="49" y="92" width="4" height="8" fill="#00CCAA" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#00CCAA" rx="2"/>
      {/* floating code particles */}
      <text x="88" y="44" fontSize="9" fill="#00FF88" opacity="0.8">{`{}`}</text>
      <text x="6"  y="56" fontSize="8" fill="#00CCFF" opacity="0.7">{`</>`}</text>
      <text x="90" y="72" fontSize="7" fill="#FFD700" opacity="0.6">{`fn`}</text>
      {/* DEV badge */}
      <rect x="2" y="72" width="28" height="13" rx="6" fill="#0f0c29" opacity="0.92"/>
      <text x="16" y="82" textAnchor="middle" fontSize="7.5" fill="#00FFCC" fontWeight="bold" fontFamily="monospace">DEV</text>
    </svg>)
  },
  // ★ EVENT EXCLUSIVE
  { id:"golden_event", name:"Золотой Юбиляр ★", price:0, emoji:"🏆", color:"#FFD700", isEvent:true,
    svg:(s=120)=>(<svg width={s} height={s} viewBox="0 0 120 120">
      <defs>
        <radialGradient id="goldbody" cx="50%" cy="50%"><stop offset="0%" stopColor="#FFF176"/><stop offset="60%" stopColor="#FFD700"/><stop offset="100%" stopColor="#B8860B"/></radialGradient>
        <radialGradient id="goldhead" cx="40%" cy="35%"><stop offset="0%" stopColor="#FFFDE7"/><stop offset="50%" stopColor="#FFD700"/><stop offset="100%" stopColor="#F9A825"/></radialGradient>
        <filter id="glow2"><feGaussianBlur stdDeviation="2.5" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      {/* glow halo */}
      <ellipse cx="60" cy="65" rx="38" ry="32" fill="#FFD700" opacity="0.18" filter="url(#glow2)"/>
      {/* body */}
      <ellipse cx="60" cy="72" rx="32" ry="24" fill="url(#goldbody)" filter="url(#glow2)"/>
      <ellipse cx="60" cy="52" rx="20" ry="18" fill="#FFD700"/>
      {/* head */}
      <circle cx="60" cy="42" r="14" fill="url(#goldhead)" filter="url(#glow2)"/>
      {/* shine on head */}
      <ellipse cx="55" cy="36" rx="5" ry="3" fill="white" opacity="0.35" transform="rotate(-20 55 36)"/>
      {/* eyes */}
      <circle cx="54" cy="38" r="4.5" fill="white"/>
      <circle cx="54" cy="38" r="2.8" fill="#8B6914"/>
      <circle cx="54" cy="38" r="1.4" fill="#1a0a00"/>
      <circle cx="54.8" cy="37.2" r="0.9" fill="white"/>
      <circle cx="66" cy="38" r="4.5" fill="white"/>
      <circle cx="66" cy="38" r="2.8" fill="#8B6914"/>
      <circle cx="66" cy="38" r="1.4" fill="#1a0a00"/>
      <circle cx="66.8" cy="37.2" r="0.9" fill="white"/>
      {/* beak */}
      <polygon points="58,44 68,46 58,49" fill="#FF8F00"/>
      {/* crown */}
      <polygon points="48,30 52,20 56,30 60,16 64,30 68,20 72,30" fill="#FFD700" stroke="#FF8F00" strokeWidth="1.2"/>
      <circle cx="60" cy="16" r="2.5" fill="#FF4500"/>
      <circle cx="52" cy="20" r="1.8" fill="#FF4500"/>
      <circle cx="68" cy="20" r="1.8" fill="#FF4500"/>
      {/* wings */}
      <ellipse cx="38" cy="78" rx="14" ry="8" fill="#F9A825" transform="rotate(-20 38 78)" filter="url(#glow2)"/>
      <ellipse cx="82" cy="78" rx="14" ry="8" fill="#F9A825" transform="rotate(20 82 78)" filter="url(#glow2)"/>
      {/* wing shine */}
      <ellipse cx="35" cy="74" rx="5" ry="2.5" fill="white" opacity="0.3" transform="rotate(-20 35 74)"/>
      {/* feet */}
      <rect x="49" y="92" width="4" height="8" fill="#FF8F00" rx="2"/>
      <rect x="65" y="92" width="4" height="8" fill="#FF8F00" rx="2"/>
      {/* sparkles */}
      <text x="18" y="52" fontSize="10" fill="#FFD700" opacity="0.9">✦</text>
      <text x="88" y="48" fontSize="8"  fill="#FFD700" opacity="0.8">✦</text>
      <text x="24" y="82" fontSize="7"  fill="#FFD700" opacity="0.7">✦</text>
      <text x="90" y="78" fontSize="9"  fill="#FFD700" opacity="0.85">✦</text>
    </svg>)
  },
];

// ══════════════════════════════════════════════════════════════
//  SHOP BOOSTS
// ══════════════════════════════════════════════════════════════
const SHOP_BOOSTS = [
  { id:"x10_3min", name:"x10 за клик", desc:"3 минуты", price:5000, duration:180000, multiplier:10, emoji:"⚡" },
];

// ══════════════════════════════════════════════════════════════
//  QUESTS
// ══════════════════════════════════════════════════════════════
const QUEST_POOL = [
  { id:"q_clicks_50",  desc:"Сделай 50 кликов",            type:"clicks",  goal:50,   reward:25  },
  { id:"q_clicks_200", desc:"Сделай 200 кликов",           type:"clicks",  goal:200,  reward:80  },
  { id:"q_crits_5",    desc:"Получи 5 критических кликов", type:"crits",   goal:5,    reward:50  },
  { id:"q_crits_20",   desc:"Получи 20 критических кликов",type:"crits",   goal:20,   reward:200 },
  { id:"q_combo_x1",   desc:"Достигни комбо ×1.3",         type:"combo",   goal:1.3,  reward:40  },
  { id:"q_level_2",    desc:"Достигни уровня 2",            type:"level",   goal:2,    reward:60  },
  { id:"q_level_5",    desc:"Достигни уровня 5",            type:"level",   goal:5,    reward:300 },
  { id:"q_passive",    desc:"Заработай 50 пассивно",        type:"passive", goal:50,   reward:100 },
  { id:"q_daily_3",    desc:"Заходи 3 дня подряд",          type:"streak",  goal:3,    reward:250, isPersistent:true },
  { id:"q_daily_7",    desc:"Заходи 7 дней подряд 🔥",      type:"streak",  goal:7,    reward:800, isPersistent:true },
];

// ══════════════════════════════════════════════════════════════
//  DAILY REWARDS
// ══════════════════════════════════════════════════════════════
const DAILY_REWARDS = [
  { day:1, type:"coins",   value:150,   label:"150 монет",           icon:"🪙" },
  { day:2, type:"coins",   value:400,   label:"400 монет",           icon:"🪙" },
  { day:3, type:"talent",  value:1,     label:"1 очко таланта",      icon:"⭐" },
  { day:4, type:"coins",   value:800,   label:"800 монет",           icon:"💰" },
  { day:5, type:"boost",   value:600,   label:"Буст x5 на 10 мин",   icon:"⚡" },
  { day:6, type:"coins",   value:2000,  label:"2 000 монет",         icon:"💰" },
  { day:7, type:"coins",   value:5000,  label:"5 000 монет + ⭐",    icon:"🎁", bonusTalent:1 },
];

function getDaysSinceEpoch() { return Math.floor(Date.now() / 86400000); }


const ADMIN_PASSWORD = "hackme";
const SESSION_KEY = "pigeon_sess3";

// ══════════════════════════════════════════════════════════════
//  WEEKEND EVENT — меняй даты каждый раз
// ══════════════════════════════════════════════════════════════
const EVENT_START = new Date("2026-03-21T00:00:00");
const EVENT_END   = new Date("2026-03-23T23:59:59");
const isEventActive = () => { const n = new Date(); return n >= EVENT_START && n <= EVENT_END; };

function loadSess() { try { return JSON.parse(localStorage.getItem(SESSION_KEY)||"null"); } catch { return null; } }
function saveSess(s) { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }

// ══════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
//  DAILY REWARD MODAL
// ══════════════════════════════════════════════════════════════
function DailyRewardModal({ streak, reward, onClaim }) {
  const [claimed, setClaimed] = useState(false);
  const [anim, setAnim] = useState(false);

  const handleClaim = () => {
    setAnim(true);
    setTimeout(() => { setClaimed(true); setTimeout(onClaim, 600); }, 400);
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.75)",backdropFilter:"blur(6px)"}}>
      <div style={{background:"linear-gradient(160deg,#1a1040,#2a1860)",border:"2px solid #FFD70066",borderRadius:28,padding:"28px 24px",maxWidth:340,width:"90%",textAlign:"center",boxShadow:"0 0 60px #FFD70033",animation:"si 0.4s cubic-bezier(0.34,1.56,0.64,1)"}}>
        <div style={{fontFamily:"'Bangers',cursive",fontSize:14,color:"#aaa",letterSpacing:3,marginBottom:6}}>ЕЖЕДНЕВНАЯ НАГРАДА</div>
        <div style={{fontFamily:"'Bangers',cursive",fontSize:36,color:"#FFD700",letterSpacing:2,textShadow:"0 0 20px #FF8800"}}>ДЕНЬ {streak}</div>

        {/* 7-day strip */}
        <div style={{display:"flex",justifyContent:"center",gap:5,margin:"16px 0"}}>
          {DAILY_REWARDS.map((r,i)=>{
            const done = i+1 < streak;
            const active = i+1 === streak;
            return (
              <div key={i} style={{
                width:36,height:44,borderRadius:10,
                background:done?"rgba(255,215,0,0.2)":active?"rgba(255,215,0,0.15)":"rgba(255,255,255,0.06)",
                border:active?"2px solid #FFD700":done?"2px solid #FFD70066":"2px solid rgba(255,255,255,0.1)",
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,
                boxShadow:active?"0 0 12px #FFD70088":"none",
              }}>
                <span style={{fontSize:done?14:active?16:12}}>{done?"✓":r.icon}</span>
                <span style={{fontSize:8,color:active?"#FFD700":"#888"}}>{r.day}</span>
              </div>
            );
          })}
        </div>

        {/* Big reward */}
        <div style={{
          background:"rgba(255,215,0,0.1)",border:"1px solid #FFD70044",borderRadius:20,
          padding:"20px",margin:"0 0 20px",
          transform:anim?"scale(1.15)":"scale(1)",transition:"transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        }}>
          <div style={{fontSize:52}}>{reward.icon}</div>
          <div style={{fontFamily:"'Bangers',cursive",fontSize:26,color:"#FFD700",marginTop:6,letterSpacing:1}}>{reward.label}</div>
        </div>

        <button
          onClick={handleClaim}
          disabled={claimed}
          style={{
            width:"100%",padding:"14px",borderRadius:16,border:"none",cursor:claimed?"default":"pointer",
            background:claimed?"rgba(255,215,0,0.2)":"linear-gradient(135deg,#FF8C00,#FFD700)",
            color:claimed?"#FFD700":"#1a0a00",fontWeight:900,fontSize:18,fontFamily:"'Nunito',sans-serif",
            letterSpacing:1,transition:"all 0.2s",
            boxShadow:claimed?"none":"0 4px 20px #FFD70066",
          }}>
          {claimed ? "✓ Получено!" : "🎁 ЗАБРАТЬ"}
        </button>
      </div>
    </div>
  );
}


function EventBackground() {
  return (
    <>
      <style>{`
        @keyframes ep1{0%{transform:translateY(100vh) rotate(0deg);opacity:0}10%{opacity:1}90%{opacity:0.6}100%{transform:translateY(-120px) rotate(720deg);opacity:0}}
        @keyframes ep2{0%{transform:translateY(100vh) translateX(0) rotate(0deg);opacity:0}10%{opacity:0.8}90%{opacity:0.4}100%{transform:translateY(-120px) translateX(40px) rotate(-360deg);opacity:0}}
        @keyframes shimmer{0%,100%{opacity:0.3}50%{opacity:0.7}}
        @keyframes hpulse{0%,100%{opacity:0.6;transform:scale(1)}50%{opacity:1;transform:scale(1.02)}}
      `}</style>
      {/* gradient overlay */}
      <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,
        background:"linear-gradient(160deg,rgba(80,50,0,0.55),rgba(40,20,0,0.4),rgba(60,40,0,0.5))",
        animation:"hpulse 4s ease-in-out infinite"}}/>
      {/* floating particles */}
      {Array.from({length:22}).map((_,i)=>{
        const size    = 6 + Math.random()*14;
        const left    = Math.random()*100;
        const delay   = Math.random()*8;
        const dur     = 6 + Math.random()*8;
        const isGold  = Math.random() > 0.35;
        const symbols = ["✦","★","◆","✸","⬡","✺"];
        const sym     = symbols[Math.floor(Math.random()*symbols.length)];
        return (
          <div key={i} style={{
            position:"fixed", left:`${left}%`, bottom:"-20px",
            fontSize:size, color: isGold?"#FFD700":"#FFF8DC",
            opacity:0, zIndex:1, pointerEvents:"none",
            animation:`${i%2===0?"ep1":"ep2"} ${dur}s ${delay}s linear infinite`,
            textShadow: isGold?"0 0 8px #FF8800,0 0 16px #FFD700":"0 0 6px white",
            filter: isGold?"drop-shadow(0 0 4px #FFB300)":"none",
          }}>{sym}</div>
        );
      })}
    </>
  );
}

function EventBanner({ onClaim, claimed }) {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,zIndex:500,pointerEvents:"none"}}>
      <div style={{
        background:"linear-gradient(90deg,#7B4F00,#D4A017,#FFD700,#D4A017,#7B4F00)",
        backgroundSize:"200% 100%",
        animation:"shimmer 2.5s ease-in-out infinite",
        padding:"7px 16px",
        display:"flex",alignItems:"center",justifyContent:"center",gap:10,
        boxShadow:"0 2px 20px #FFD70066",pointerEvents:"auto",
      }}>
        <span style={{fontSize:18}}>🏆</span>
        <span style={{fontFamily:"'Bangers',cursive",fontSize:16,color:"#1a0a00",letterSpacing:1.5}}>
          НАМ 1 НЕДЕЛЯ! ИВЕНТ ДО 23 МАРТА
        </span>
        {!claimed
          ? <button onClick={onClaim} style={{background:"#1a0a00",color:"#FFD700",border:"none",borderRadius:10,padding:"4px 12px",fontWeight:900,fontSize:12,cursor:"pointer",fontFamily:"'Nunito',sans-serif"}}>ЗАБРАТЬ СКИН ★</button>
          : <span style={{fontSize:12,color:"#1a0a00",fontWeight:900}}>✓ Скин получен!</span>
        }
        <button onClick={()=>setVisible(false)} style={{background:"none",border:"none",color:"#1a0a00",fontSize:18,cursor:"pointer",padding:"0 4px",lineHeight:1,pointerEvents:"auto"}}>✕</button>
      </div>
    </div>
  );
}


function Particle({ x, y, text, color, onDone }) {
  const [opacity, setOpacity] = useState(1);
  const [py, setPy] = useState(y);
  useEffect(() => {
    let f, t0 = null;
    const animate = (ts) => { if (!t0) t0=ts; const p=(ts-t0)/750; setOpacity(1-p); setPy(y-80*p); if(p<1) f=requestAnimationFrame(animate); else onDone(); };
    f = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(f);
  }, []);
  return <div style={{ position:"fixed",left:x-30,top:py,opacity,pointerEvents:"none",fontSize:22,fontWeight:900,color,textShadow:`0 0 12px ${color}`,zIndex:9999,userSelect:"none",fontFamily:"'Nunito',sans-serif",whiteSpace:"nowrap" }}>{text}</div>;
}

function LevelUpOverlay({ level, onDone }) {
  const [ph, setPh] = useState("in");
  useEffect(() => {
    const t1=setTimeout(()=>setPh("hold"),60);
    const t2=setTimeout(()=>setPh("out"),1900);
    const t3=setTimeout(onDone,2500);
    return ()=>{clearTimeout(t1);clearTimeout(t2);clearTimeout(t3);};
  },[]);
  return (
    <div style={{ position:"fixed",inset:0,zIndex:8888,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none",background:ph==="hold"?"rgba(0,0,0,0.55)":"transparent",opacity:ph==="out"?0:1,transition:"background 0.3s,opacity 0.5s" }}>
      {Array.from({length:12}).map((_,i)=>(
        <div key={i} style={{ position:"absolute",width:3,height:ph==="hold"?230:0,background:"linear-gradient(to top,transparent,#FFD700BB)",borderRadius:3,transformOrigin:"bottom center",transform:`rotate(${i*30}deg) translateX(-50%)`,left:"50%",bottom:"50%",transition:"height 0.4s cubic-bezier(0.34,1.56,0.64,1)",opacity:ph==="out"?0:0.8 }}/>
      ))}
      <div style={{ transform:ph==="hold"?"scale(1)":ph==="out"?"scale(1.2)":"scale(0.3)",opacity:ph==="hold"?1:0,transition:"transform 0.45s cubic-bezier(0.34,1.56,0.64,1),opacity 0.4s",textAlign:"center" }}>
        <div style={{ fontFamily:"'Bangers',cursive",fontSize:22,color:"#FFD700",letterSpacing:6,textShadow:"0 0 20px #FF8800",marginBottom:4 }}>УРОВЕНЬ ВЫШЕ!</div>
        <div style={{ fontFamily:"'Bangers',cursive",fontSize:100,color:"#FFD700",lineHeight:1,textShadow:"0 0 40px #FF8800,0 0 80px #FF4400,0 5px 0 #883300" }}>{level}</div>
        <div style={{ fontSize:38,marginTop:6 }}>🎉🐦🎉</div>
      </div>
    </div>
  );
}

function MilestoneToast({ ms, onDone }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => { const t=setTimeout(()=>{setVisible(false);setTimeout(onDone,400);},2200); return()=>clearTimeout(t); },[]);
  return (
    <div style={{ position:"fixed",top:80,left:"50%",transform:`translateX(-50%) scale(${visible?1:0.8})`,opacity:visible?1:0,transition:"all 0.4s cubic-bezier(0.34,1.56,0.64,1)",zIndex:8800,pointerEvents:"none",background:"rgba(20,10,40,0.95)",border:"2px solid #FFD700",borderRadius:20,padding:"12px 28px",textAlign:"center",backdropFilter:"blur(12px)" }}>
      <div style={{ fontFamily:"'Bangers',cursive",fontSize:22,color:"#FFD700",letterSpacing:2 }}>{ms.label}</div>
      <div style={{ color:"#ccc",fontSize:13,marginTop:2 }}>{ms.desc}</div>
    </div>
  );
}

function RandomEventToast({ event, onDone }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => { const t=setTimeout(()=>{setVisible(false);setTimeout(onDone,400);},3000); return()=>clearTimeout(t); },[]);
  return (
    <div style={{ position:"fixed",top:140,left:"50%",transform:`translateX(-50%) scale(${visible?1:0.7})`,opacity:visible?1:0,transition:"all 0.35s cubic-bezier(0.34,1.56,0.64,1)",zIndex:8700,pointerEvents:"none",background:"rgba(80,20,120,0.97)",border:"2px solid #CC44FF",borderRadius:20,padding:"14px 32px",textAlign:"center",backdropFilter:"blur(12px)" }}>
      <div style={{ fontSize:28 }}>{event.icon}</div>
      <div style={{ fontFamily:"'Bangers',cursive",fontSize:20,color:"#CC44FF",letterSpacing:2,marginTop:2 }}>{event.name}</div>
      <div style={{ color:"#ccc",fontSize:13,marginTop:2 }}>{event.desc}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  HELPER PIGEON MUTATIONS
// ══════════════════════════════════════════════════════════════
const HELPER_PIGEONS = [
  {
    id: "heavenly",
    name: "Небесный Голубь",
    bonus: "+150 благодати",
    reward: 150,
    color: "#E0F7FF",
    enter: "translateX(110%) translateY(-30%)",
    exit:  "translateX(110%) translateY(-120%)",
    mid:   "translateX(0%) translateY(0%)",
    pos:   { bottom:"30%", right:0 },
    animDur: 5500,
    svg: (s=110) => (
      <svg width={s} height={s} viewBox="0 0 120 120">
        <defs>
          <radialGradient id="hv1" cx="50%" cy="40%"><stop offset="0%" stopColor="#FFFFFF"/><stop offset="60%" stopColor="#B3E5FC"/><stop offset="100%" stopColor="#81D4FA"/></radialGradient>
          <filter id="halo"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <ellipse cx="60" cy="65" rx="42" ry="30" fill="#E1F5FE" opacity="0.25" filter="url(#halo)"/>
        <ellipse cx="60" cy="72" rx="30" ry="22" fill="url(#hv1)"/>
        <ellipse cx="60" cy="52" rx="19" ry="17" fill="#E1F5FE"/>
        <circle  cx="60" cy="42" r="13" fill="white" filter="url(#halo)"/>
        {/* halo ring */}
        <ellipse cx="60" cy="27" rx="14" ry="4" fill="none" stroke="#FFD700" strokeWidth="2.5" opacity="0.85"/>
        {/* eyes */}
        <circle cx="54" cy="38" r="4" fill="white"/><circle cx="54" cy="38" r="2.2" fill="#1565C0"/><circle cx="54.8" cy="37.2" r="0.8" fill="white"/>
        <circle cx="66" cy="38" r="4" fill="white"/><circle cx="66" cy="38" r="2.2" fill="#1565C0"/><circle cx="66.8" cy="37.2" r="0.8" fill="white"/>
        <polygon points="58,44 67,46 58,49" fill="#FFD700"/>
        {/* wings spread */}
        <ellipse cx="30" cy="68" rx="18" ry="9" fill="#B3E5FC" opacity="0.9" transform="rotate(-30 30 68)"/>
        <ellipse cx="90" cy="68" rx="18" ry="9" fill="#B3E5FC" opacity="0.9" transform="rotate(30 90 68)"/>
        {/* sparkles */}
        <text x="10" y="50" fontSize="12" fill="#FFD700" opacity="0.8">✦</text>
        <text x="95" y="44" fontSize="9"  fill="#FFD700" opacity="0.7">✦</text>
        <text x="18" y="80" fontSize="8"  fill="white"   opacity="0.6">✦</text>
        <rect x="50" y="92" width="4" height="7" fill="#90CAF9" rx="2"/>
        <rect x="64" y="92" width="4" height="7" fill="#90CAF9" rx="2"/>
      </svg>
    )
  },
  {
    id: "demonic",
    name: "Демонический Голубь",
    bonus: "+200 душ",
    reward: 200,
    color: "#FF3030",
    enter: "translateX(-110%) translateY(40%)",
    exit:  "translateX(-110%) translateY(40%)",
    mid:   "translateX(0%) translateY(0%)",
    pos:   { bottom:"25%", left:0 },
    animDur: 5000,
    svg: (s=110) => (
      <svg width={s} height={s} viewBox="0 0 120 120">
        <defs>
          <radialGradient id="dm1" cx="50%" cy="50%"><stop offset="0%" stopColor="#8B0000"/><stop offset="70%" stopColor="#4A0000"/><stop offset="100%" stopColor="#200000"/></radialGradient>
          <filter id="dfire"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <ellipse cx="60" cy="68" rx="40" ry="26" fill="#8B0000" opacity="0.2" filter="url(#dfire)"/>
        <ellipse cx="60" cy="72" rx="30" ry="22" fill="url(#dm1)"/>
        <ellipse cx="60" cy="52" rx="19" ry="17" fill="#6B0000"/>
        <circle  cx="60" cy="42" r="13" fill="#8B0000" filter="url(#dfire)"/>
        {/* horns */}
        <polygon points="50,30 46,14 54,28" fill="#CC0000"/>
        <polygon points="70,30 74,14 66,28" fill="#CC0000"/>
        {/* eyes */}
        <circle cx="54" cy="38" r="4.5" fill="#FF0000"/><circle cx="54" cy="38" r="2.5" fill="#800000"/><circle cx="54.8" cy="37.2" r="0.9" fill="#FF6060"/>
        <circle cx="66" cy="38" r="4.5" fill="#FF0000"/><circle cx="66" cy="38" r="2.5" fill="#800000"/><circle cx="66.8" cy="37.2" r="0.9" fill="#FF6060"/>
        <polygon points="57,44 68,46 57,50" fill="#FF4400"/>
        {/* bat wings */}
        <path d="M28 75 Q10 55 16 40 Q22 50 30 65 Z" fill="#4A0000" opacity="0.9"/>
        <path d="M92 75 Q110 55 104 40 Q98 50 90 65 Z" fill="#4A0000" opacity="0.9"/>
        <path d="M28 75 Q18 42 16 40" stroke="#8B0000" strokeWidth="1.5" fill="none"/>
        <path d="M92 75 Q102 42 104 40" stroke="#8B0000" strokeWidth="1.5" fill="none"/>
        {/* tail */}
        <path d="M45 90 Q50 105 60 100 Q70 105 75 90" fill="#6B0000" stroke="#8B0000" strokeWidth="1"/>
        <rect x="50" y="92" width="4" height="7" fill="#8B0000" rx="2"/>
        <rect x="64" y="92" width="4" height="7" fill="#8B0000" rx="2"/>
        <text x="8"  y="65" fontSize="11" fill="#FF4400" opacity="0.9">🔥</text>
        <text x="94" y="60" fontSize="9"  fill="#FF4400" opacity="0.8">🔥</text>
      </svg>
    )
  },
  {
    id: "youtuber",
    name: "Ютуберский Голубь",
    bonus: "+100 просмотров",
    reward: 100,
    color: "#FF0000",
    enter: "translateX(0%) translateY(-120%)",
    exit:  "translateX(60%) translateY(-120%)",
    mid:   "translateX(0%) translateY(0%)",
    pos:   { top:"18%", left:"50%", transform:"translateX(-50%)" },
    animDur: 6000,
    svg: (s=110) => (
      <svg width={s} height={s} viewBox="0 0 120 120">
        <defs>
          <radialGradient id="yt1" cx="50%" cy="40%"><stop offset="0%" stopColor="#FF8A80"/><stop offset="100%" stopColor="#D32F2F"/></radialGradient>
        </defs>
        <ellipse cx="60" cy="72" rx="30" ry="22" fill="#EF9A9A"/>
        <ellipse cx="60" cy="52" rx="19" ry="17" fill="#FFCDD2"/>
        <circle  cx="60" cy="42" r="13" fill="#FFCDD2"/>
        {/* sunglasses */}
        <rect x="44" y="35" width="13" height="9" rx="4" fill="#111" opacity="0.95"/>
        <rect x="61" y="35" width="13" height="9" rx="4" fill="#111" opacity="0.95"/>
        <line x1="57" y1="39" x2="61" y2="39" stroke="#555" strokeWidth="1.5"/>
        <rect x="44" y="35" width="3" height="9" rx="2" fill="#FF0000" opacity="0.7"/>
        <rect x="74" y="35" width="3" height="9" rx="2" fill="#FF0000" opacity="0.7"/>
        <polygon points="58,44 68,46 58,50" fill="#FF7043"/>
        {/* camera */}
        <rect x="76" y="48" width="22" height="15" rx="3" fill="#222"/>
        <circle cx="86" cy="55" r="5" fill="#333"/><circle cx="86" cy="55" r="3" fill="#111"/><circle cx="86" cy="55" r="1.5" fill="#555"/>
        <rect x="94" y="51" width="5" height="8" rx="2" fill="#444"/>
        <circle cx="78" cy="50" r="2" fill="#FF0000"/>
        {/* merch cap */}
        <path d="M48 30 Q60 20 72 30 L70 34 Q60 26 50 34 Z" fill="#FF0000"/>
        <rect x="44" y="30" width="32" height="5" rx="2" fill="#CC0000"/>
        {/* wings */}
        <ellipse cx="32" cy="70" rx="16" ry="8" fill="#EF9A9A" transform="rotate(-25 32 70)"/>
        <ellipse cx="88" cy="70" rx="16" ry="8" fill="#EF9A9A" transform="rotate(25 88 70)"/>
        <rect x="50" y="92" width="4" height="7" fill="#FF7043" rx="2"/>
        <rect x="64" y="92" width="4" height="7" fill="#FF7043" rx="2"/>
        {/* subscribe button */}
        <rect x="30" y="100" width="60" height="14" rx="7" fill="#FF0000"/>
        <text x="60" y="111" textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">ПОДПИСАТЬСЯ</text>
      </svg>
    )
  },
  {
    id: "economic",
    name: "Экономный Голубь",
    bonus: "$$$",
    reward: 300,
    color: "#00C853",
    enter: "translateX(110%) translateY(20%)",
    exit:  "translateX(110%) translateY(20%)",
    mid:   "translateX(0%) translateY(0%)",
    pos:   { bottom:"20%", right:0 },
    animDur: 7000,
    svg: (s=110) => (
      <svg width={s} height={s} viewBox="0 0 120 120">
        <defs>
          <radialGradient id="ec1" cx="50%" cy="40%"><stop offset="0%" stopColor="#C8E6C9"/><stop offset="100%" stopColor="#388E3C"/></radialGradient>
        </defs>
        <ellipse cx="60" cy="72" rx="30" ry="22" fill="url(#ec1)"/>
        <ellipse cx="60" cy="52" rx="19" ry="17" fill="#A5D6A7"/>
        <circle  cx="60" cy="42" r="13" fill="#C8E6C9"/>
        {/* top hat */}
        <rect x="48" y="16" width="24" height="16" rx="2" fill="#1B5E20"/>
        <rect x="43" y="29" width="34" height="5" rx="2" fill="#2E7D32"/>
        <text x="56" y="27" fontSize="9" fill="#FFD700" fontWeight="bold">$</text>
        {/* monocle */}
        <circle cx="66" cy="38" r="5.5" fill="none" stroke="#FFD700" strokeWidth="1.5"/>
        <line x1="70" y1="40" x2="73" y2="43" stroke="#FFD700" strokeWidth="1.2"/>
        {/* eyes */}
        <circle cx="54" cy="38" r="4" fill="white"/><circle cx="54" cy="38" r="2.2" fill="#1B5E20"/><circle cx="54.8" cy="37.2" r="0.8" fill="white"/>
        <circle cx="66" cy="38" r="4" fill="white"/><circle cx="66" cy="38" r="2.2" fill="#1B5E20"/><circle cx="66.8" cy="37.2" r="0.8" fill="white"/>
        {/* beak pecking down */}
        <polygon points="56,48 68,52 56,54" fill="#FFD700"/>
        {/* briefcase */}
        <rect x="38" y="75" width="18" height="14" rx="3" fill="#4E342E"/>
        <rect x="43" y="72" width="8" height="5" rx="2" fill="#6D4C41"/>
        <line x1="38" y1="82" x2="56" y2="82" stroke="#795548" strokeWidth="1"/>
        <text x="44" y="86" fontSize="8" fill="#FFD700" fontWeight="bold">$</text>
        {/* wings */}
        <ellipse cx="32" cy="70" rx="15" ry="8" fill="#81C784" transform="rotate(-20 32 70)"/>
        <ellipse cx="88" cy="70" rx="15" ry="8" fill="#81C784" transform="rotate(20 88 70)"/>
        <rect x="50" y="92" width="4" height="7" fill="#FFD700" rx="2"/>
        <rect x="64" y="92" width="4" height="7" fill="#FFD700" rx="2"/>
      </svg>
    )
  },
];

// ──────────────────────────────────────────────────────────────
//  HelperPigeon animated component
// ──────────────────────────────────────────────────────────────
function HelperPigeon({ pigeon, onCollect }) {
  const [phase, setPhase] = useState("enter"); // enter → idle → exit
  const [dollars, setDollars] = useState([]);
  const dollarId = useRef(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("idle"), 700);
    const t2 = setTimeout(() => setPhase("exit"), pigeon.animDur - 900);
    const t3 = setTimeout(onCollect, pigeon.animDur);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Economic pigeon pecks and spawns dollar signs
  useEffect(() => {
    if (pigeon.id !== "economic") return;
    const iv = setInterval(() => {
      const id = dollarId.current++;
      setDollars(d => [...d, { id, x: Math.random()*60-30, delay: 0 }]);
      setTimeout(() => setDollars(d => d.filter(x => x.id !== id)), 1400);
    }, 300);
    return () => clearInterval(iv);
  }, []);

  const transform = phase === "idle" ? pigeon.mid : phase === "exit" ? pigeon.exit : pigeon.enter;
  const isIdle = phase === "idle";

  return (
    <div style={{
      position:"fixed", zIndex:7000, pointerEvents:"auto",
      ...pigeon.pos,
      transform,
      transition: phase==="enter"
        ? "transform 0.7s cubic-bezier(0.34,1.56,0.64,1)"
        : phase==="exit"
          ? "transform 0.9s cubic-bezier(0.4,0,0.6,1)"
          : "none",
    }}>
      {/* Dollar signs for economic pigeon */}
      {dollars.map(d => (
        <div key={d.id} style={{
          position:"absolute", left:`calc(50% + ${d.x}px)`, bottom:"60%",
          fontSize:18, fontWeight:900, color:"#00C853",
          textShadow:"0 0 8px #00C853",
          animation:"ep1 1.3s ease-out forwards",
          pointerEvents:"none", zIndex:7001,
        }}>$</div>
      ))}

      {/* Name badge */}
      {isIdle && (
        <div style={{
          position:"absolute", top:-32, left:"50%", transform:"translateX(-50%)",
          background:"rgba(0,0,0,0.85)", border:`1px solid ${pigeon.color}`,
          color:pigeon.color, borderRadius:10, padding:"3px 10px",
          fontSize:11, fontWeight:900, whiteSpace:"nowrap",
          fontFamily:"'Nunito',sans-serif", textShadow:`0 0 8px ${pigeon.color}`,
          animation:"si 0.3s",
        }}>
          {pigeon.name}
        </div>
      )}

      {/* The pigeon itself — clickable to collect early */}
      <div
        onClick={() => { setPhase("exit"); setTimeout(onCollect, 900); }}
        style={{
          cursor:"pointer",
          filter:`drop-shadow(0 0 12px ${pigeon.color}) drop-shadow(0 0 24px ${pigeon.color}88)`,
          animation: isIdle
            ? pigeon.id==="economic" ? "pb 0.5s ease-in-out infinite"
            : pigeon.id==="demonic"  ? "fg 1.2s ease-in-out infinite"
            : pigeon.id==="youtuber" ? "fg 1.8s ease-in-out infinite"
            : "fg 2.5s ease-in-out infinite"
          : "none",
        }}
      >
        {pigeon.svg(120)}
      </div>

      {/* Bonus label */}
      {isIdle && (
        <div style={{
          textAlign:"center", marginTop:4,
          color:pigeon.color, fontWeight:900, fontSize:12,
          fontFamily:"'Nunito',sans-serif",
          textShadow:`0 0 10px ${pigeon.color}`,
          animation:"si 0.4s",
        }}>
          {pigeon.bonus}
        </div>
      )}
    </div>
  );
}

const RANDOM_EVENTS = [
  { id:"golden_coin",  icon:"🪙",  name:"Золотая монета!",   desc:"+200 очков",        effect:(g)=>({...g, totalClicks:g.totalClicks+200*g.coinMult, clicks:g.clicks+200*g.coinMult}) },
  { id:"flock",        icon:"🕊️",  name:"Стая голубей!",     desc:"+5 сек x3 комбо",   effect:(g)=>({...g, comboMult:Math.min(1.5,g.comboMult+0.3), comboEndTime:Date.now()+5000}) },
  { id:"pigeon_god",   icon:"✨",  name:"Голубиный Бог!",    desc:"+50% на 10 сек",    effect:(g)=>({...g, activeBoost:{multiplier:Math.round((g.activeBoost?.multiplier||1)*1.5+1),endsAt:Date.now()+10000}}) },
  { id:"breadcrumbs",  icon:"🍞",  name:"Хлебные крошки!",   desc:"+100 очков",        effect:(g)=>({...g, totalClicks:g.totalClicks+100, clicks:g.clicks+100}) },
];

// ══════════════════════════════════════════════════════════════
//  COMPUTE CLICK VALUE
// ══════════════════════════════════════════════════════════════
function computeClickValue({ talents, level, activeBoost, comboMult, critChanceBonus, boostPower }) {
  const clickPlusBase = (talents.click_plus1||0);
  const levelBonus = 1 + (level-1)*0.05;
  const critChance = 0.10 + (talents.crit_chance||0)*0.02 + critChanceBonus;
  const isCrit = Math.random() < critChance;
  const boostMult = (activeBoost && Date.now() < activeBoost.endsAt)
    ? activeBoost.multiplier * (1 + (talents.boost_power||0)*0.5 * boostPower)
    : 1;
  const base = (1 + clickPlusBase) * levelBonus * comboMult * boostMult;
  const value = isCrit ? Math.round(base * 3) : Math.round(base);
  return { value, isCrit };
}

// ══════════════════════════════════════════════════════════════
//  MAIN APP
// ══════════════════════════════════════════════════════════════
const INIT_STATE = () => ({
  playerName:"", clicks:0, totalClicks:0, allTimeClicks:0,
  level:1, xp:0, talentPoints:0,
  talents:{},
  currentSkin:"default", ownedSkins:["default"], customSkins:[],
  activeBoost:null,
  comboMult:1, comboEndTime:0, lastClickTime:0,
  passiveTotal:0,
  questProgress:{ crits:0, passive:0 },
  completedQuests:[],
  activeQuests:["q_clicks_50","q_level_2","q_daily_3","q_daily_7"],
  prestigeCount:0, prestigeBonus:1,
  lastDailyDay:0, dailyStreak:0,
  coinMult:1,
});

export default function App() {
  const [screen, setScreen] = useState("intro");
  const [gs, setGs] = useState(INIT_STATE());
  const [eventActive] = useState(isEventActive);
  const [nameInput, setNameInput] = useState("");
  const [particles, setParticles] = useState([]);
  const [pigeonAnim, setPigeonAnim] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpNum, setLevelUpNum] = useState(1);
  const [toastMs, setToastMs] = useState(null);
  const [randomEvent, setRandomEvent] = useState(null);
  const [helperPigeon, setHelperPigeon] = useState(null);
  const [showDaily, setShowDaily] = useState(false);
  const [pendingDaily, setPendingDaily] = useState(null);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminPw, setAdminPw] = useState("");
  const [adminError, setAdminError] = useState("");
  const [adminMsg, setAdminMsg] = useState("");
  const [adminClicks, setAdminClicks] = useState("");
  const [adminLevel, setAdminLevel] = useState("");
  const [skinUpName, setSkinUpName] = useState("");
  const [skinUpPrice, setSkinUpPrice] = useState("500");
  const [skinUpImg, setSkinUpImg] = useState(null);
  const [talentTab, setTalentTab] = useState("active");
  const [boostTimeLeft, setBoostTimeLeft] = useState(0);
  const [milestoneX2Active, setMilestoneX2Active] = useState(false);
  const [visualEffect, setVisualEffect] = useState(false);
  const [critUnlocked, setCritUnlocked] = useState(false);
  const partId = useRef(0);
  const gsRef = useRef(gs);
  const levelRef = useRef(1);

  useEffect(() => { gsRef.current = gs; levelRef.current = gs.level; }, [gs]);

  // Lock body scroll on game screen, unlock on others
  useEffect(() => {
    if (screen === "game") {
      document.body.classList.add("game-screen");
    } else {
      document.body.classList.remove("game-screen");
    }
    return () => document.body.classList.remove("game-screen");
  }, [screen]);

  // Load session
  useEffect(() => {
    const s = loadSess();
    if (s && s.playerName) {
      setGs(g=>({...g,...s}));
      setScreen("game");
      // Check daily after mount
      setTimeout(() => {
        const today = getDaysSinceEpoch();
        const lastDay = s.lastDailyDay || 0;
        if (today > lastDay) {
          const isConsecutive = today - lastDay === 1;
          const streak = isConsecutive ? Math.min((s.dailyStreak||0)+1, 7) : 1;
          const reward = DAILY_REWARDS[streak-1];
          setPendingDaily({ reward, streak });
          setShowDaily(true);
        }
      }, 800);
    }
  },[]);

  // Save session
  useEffect(() => {
    if (gs.playerName) saveSess(gs);
  }, [gs]);

  // Passive income tick
  useEffect(() => {
    if (!gs.playerName) return;
    const iv = setInterval(() => {
      const g = gsRef.current;
      const base = (g.talents.passive_inc||0);
      if (base===0) return;
      const genBoost = 1+(g.talents.gen_boost||0)*0.1;
      const prestigeB = g.prestigeBonus||1;
      const income = Math.round(base * genBoost * prestigeB);
      if (income===0) return;
      setGs(prev=>({
        ...prev,
        clicks:prev.clicks+income,
        totalClicks:prev.totalClicks+income,
        allTimeClicks:prev.allTimeClicks+income,
        passiveTotal:prev.passiveTotal+income,
        questProgress:{...prev.questProgress,passive:(prev.questProgress.passive||0)+income},
      }));
    },1000);
    return ()=>clearInterval(iv);
  },[gs.playerName]);

  // Boost countdown
  useEffect(() => {
    const iv = setInterval(()=>{
      if (!gsRef.current.activeBoost) return;
      const left = gsRef.current.activeBoost.endsAt - Date.now();
      if (left<=0) { setGs(g=>({...g,activeBoost:null})); setBoostTimeLeft(0); }
      else setBoostTimeLeft(Math.ceil(left/1000));
    },250);
    return ()=>clearInterval(iv);
  },[]);

  // Combo decay
  useEffect(() => {
    const iv = setInterval(()=>{
      const g = gsRef.current;
      if (g.comboMult<=1) return;
      if (Date.now()-g.lastClickTime>2000 && Date.now()>g.comboEndTime) {
        setGs(prev=>({...prev,comboMult:Math.max(1,prev.comboMult-0.05)}));
      }
    },500);
    return ()=>clearInterval(iv);
  },[]);

  // Random events
  useEffect(() => {
    if (!gs.playerName) return;
    const base = 30000;
    const boost = (gs.talents.event_chance||0)*0.25;
    const interval = base*(1-boost);
    const iv = setInterval(()=>{
      if (Math.random()<0.4) {
        const ev = RANDOM_EVENTS[Math.floor(Math.random()*RANDOM_EVENTS.length)];
        setRandomEvent(ev);
        setGs(prev=>{
          const bonus = (ev.id==="golden_coin"||ev.id==="breadcrumbs") ? {coinMult:1+(prev.talents.coin_bonus||0)} : {};
          return ev.effect({...prev,...bonus});
        });
      }
    },interval);
    return ()=>clearInterval(iv);
  },[gs.playerName,gs.talents]);

  // Helper pigeon mutations — rare normally, frequent with dev skin
  useEffect(() => {
    if (!gs.playerName) return;
    const isDevMode = gs.currentSkin === "capybara";
    const iv = setInterval(() => {
      if (helperPigeon) return;
      const chance = isDevMode ? 0.85 : 0.35;
      if (Math.random() < chance) {
        const p = HELPER_PIGEONS[Math.floor(Math.random()*HELPER_PIGEONS.length)];
        setHelperPigeon(p);
      }
    }, isDevMode ? 8000 : 45000 + Math.random()*60000);
    return () => clearInterval(iv);
  }, [gs.playerName, gs.currentSkin, helperPigeon]);


  useEffect(() => {
    const g = gsRef.current;
    g.activeQuests?.forEach(qid=>{
      const q = QUEST_POOL.find(x=>x.id===qid);
      if (!q||g.completedQuests.includes(qid)) return;
      let done = false;
      if (q.type==="clicks" && g.allTimeClicks>=q.goal) done=true;
      if (q.type==="crits" && (g.questProgress.crits||0)>=q.goal) done=true;
      if (q.type==="level" && g.level>=q.goal) done=true;
      if (q.type==="combo" && g.comboMult>=q.goal) done=true;
      if (q.type==="passive" && (g.questProgress.passive||0)>=q.goal) done=true;
      if (q.type==="streak" && (g.dailyStreak||0)>=q.goal) done=true;
      if (done) {
        setGs(prev=>{
          const newCompleted = [...prev.completedQuests,qid];
          const newActive = prev.activeQuests.filter(x=>x!==qid);
          const available = QUEST_POOL.filter(x=>!newCompleted.includes(x.id)&&!newActive.includes(x.id));
          const nextQ = available[Math.floor(Math.random()*available.length)];
          return {
            ...prev,
            completedQuests:newCompleted,
            activeQuests:nextQ?[...newActive,nextQ.id]:newActive,
            totalClicks:prev.totalClicks+q.reward,
            allTimeClicks:prev.allTimeClicks+q.reward,
            clicks:prev.clicks+q.reward,
          };
        });
      }
    });
  },[gs.allTimeClicks,gs.questProgress,gs.level,gs.comboMult]);

  // XP milestone triggers
  useEffect(() => {
    const g = gs;
    const threshold = levelThreshold(g.level);
    const pct = (g.xp / threshold)*100;
    if (pct>=25 && !g.milestonesSeen?.[`${g.level}_25`]) {
      setGs(prev=>({...prev,milestonesSeen:{...prev.milestonesSeen,[`${g.level}_25`]:true}}));
      setVisualEffect(true);
      setToastMs(MILESTONES[0]);
      setTimeout(()=>setVisualEffect(false),4000);
    }
    if (pct>=50 && !g.milestonesSeen?.[`${g.level}_50`]) {
      setGs(prev=>({...prev,milestonesSeen:{...prev.milestonesSeen,[`${g.level}_50`]:true}}));
      setCritUnlocked(true);
      setToastMs(MILESTONES[1]);
      setTimeout(()=>setCritUnlocked(false),10000);
    }
    if (pct>=75 && !g.milestonesSeen?.[`${g.level}_75`]) {
      setGs(prev=>({...prev,milestonesSeen:{...prev.milestonesSeen,[`${g.level}_75`]:true},activeBoost:{multiplier:2,endsAt:Date.now()+30000}}));
      setMilestoneX2Active(true);
      setToastMs(MILESTONES[2]);
      setTimeout(()=>setMilestoneX2Active(false),30000);
    }
  },[gs.xp,gs.level]);

  const handleClick = useCallback((e)=>{
    const x=e.clientX, y=e.clientY;
    const g = gsRef.current;
    const threshold = levelThreshold(g.level);

    // Combo
    const now = Date.now();
    const timeSince = now - g.lastClickTime;
    const comboInc = timeSince<600 ? 0.01*(1+(g.talents.combo_pct||0)*0.1) : 0;
    const newCombo = Math.min(1.5, g.comboMult+comboInc);

    // Compute value
    const devBoost = g.currentSkin==="capybara" ? {multiplier:10, endsAt:Infinity} : g.activeBoost;
    const {value,isCrit} = computeClickValue({
      talents:g.talents, level:g.level,
      activeBoost: devBoost || g.activeBoost,
      comboMult:newCombo,
      critChanceBonus: critUnlocked?0.1:0,
      boostPower:1,
    });
    const ptxtColor = g.currentSkin==="capybara" ? "#00FFCC" : isCrit?"#FF4444":value>1?"#FF8800":"#FFD700";

    const pid = partId.current++;
    const ptxt = isCrit?`КРИТ! +${value}`:`+${value}`;
    const pcol = ptxtColor;
    setParticles(p=>[...p,{id:pid,x,y,text:ptxt,color:pcol}]);

    setPigeonAnim(true);
    setTimeout(()=>setPigeonAnim(false),150);

    setGs(prev=>{
      const newXP = prev.xp + value;
      const newClicks = prev.clicks + value;
      const newTotal = prev.totalClicks + value;
      const newAllTime = prev.allTimeClicks + value;
      const newCrits = isCrit ? (prev.questProgress.crits||0)+1 : (prev.questProgress.crits||0);

      let lvl = prev.level;
      let xp = newXP;
      let talentGain = 0;
      let didLevelUp = false;

      if (newXP >= threshold) {
        lvl = prev.level+1;
        xp = newXP - threshold;
        talentGain = lvl%2===0?1:0;
        didLevelUp = true;
        levelRef.current = lvl;
        setTimeout(()=>{setLevelUpNum(lvl);setShowLevelUp(true);},50);
      }

      return {
        ...prev,
        clicks:newClicks,
        totalClicks:newTotal,
        allTimeClicks:newAllTime,
        xp, level:lvl,
        talentPoints:prev.talentPoints+talentGain,
        comboMult:didLevelUp?1:newCombo,
        lastClickTime:now,
        questProgress:{...prev.questProgress,crits:newCrits},
      };
    });
  },[critUnlocked]);

  const removeParticle = useCallback((id)=>setParticles(p=>p.filter(x=>x.id!==id)),[]);

  const buySkin = (skin)=>{
    if (gs.ownedSkins.includes(skin.id)) { setGs(g=>({...g,currentSkin:skin.id})); return; }
    if (gs.totalClicks>=skin.price) {
      const disc = 1-(gs.talents.gen_cheap||0)*0.05;
      const price = Math.round(skin.price*disc);
      if (gs.totalClicks<price) return;
      setGs(g=>({...g,totalClicks:g.totalClicks-price,clicks:Math.max(0,g.clicks-price),ownedSkins:[...g.ownedSkins,skin.id],currentSkin:skin.id}));
    }
  };

  const buyBoost = (boost)=>{
    if (gs.totalClicks<boost.price) return;
    const mult = boost.multiplier*(1+(gs.talents.boost_power||0)*0.5);
    setGs(g=>({...g,totalClicks:g.totalClicks-boost.price,clicks:Math.max(0,g.clicks-boost.price),activeBoost:{multiplier:Math.round(mult),endsAt:Date.now()+boost.duration}}));
  };

  const investTalent = (tid)=>{
    if (gs.talentPoints<=0) return;
    const allTalents = Object.values(TALENT_TREES).flatMap(t=>t.talents);
    const t = allTalents.find(x=>x.id===tid);
    if (!t) return;
    const cur = gs.talents[tid]||0;
    if (cur>=t.max) return;
    setGs(g=>({...g,talentPoints:g.talentPoints-1,talents:{...g.talents,[tid]:(g.talents[tid]||0)+1}}));
  };

  const handlePrestige = ()=>{
    if (gs.level<20) return;
    const newPrestige = gs.prestigeCount+1;
    setGs({...INIT_STATE(),
      playerName:gs.playerName,
      prestigeCount:newPrestige,
      prestigeBonus:1+newPrestige*0.25,
      ownedSkins:gs.ownedSkins,
      customSkins:gs.customSkins,
      currentSkin:gs.currentSkin,
      allTimeClicks:gs.allTimeClicks,
      completedQuests:gs.completedQuests,
    });
  };

  const claimDaily = useCallback(() => {
    if (!pendingDaily) return;
    const { reward, streak } = pendingDaily;
    setGs(prev => {
      const coins = reward.type==="coins" ? reward.value : reward.type==="boost" ? 0 : 0;
      const talentBonus = (reward.type==="talent" ? reward.value : 0) + (reward.bonusTalent||0);
      const newBoost = reward.type==="boost"
        ? { multiplier:5, endsAt:Date.now()+600000 }
        : prev.activeBoost;
      return {
        ...prev,
        clicks: prev.clicks + coins,
        totalClicks: prev.totalClicks + coins,
        allTimeClicks: prev.allTimeClicks + coins,
        talentPoints: prev.talentPoints + talentBonus,
        activeBoost: newBoost,
        lastDailyDay: getDaysSinceEpoch(),
        dailyStreak: streak,
      };
    });
    setShowDaily(false);
    setPendingDaily(null);
  }, [pendingDaily]);

  const collectHelperPigeon = useCallback(() => {
    if (!helperPigeon) return;
    const reward = helperPigeon.reward;
    setGs(prev => ({
      ...prev,
      clicks: prev.clicks + reward,
      totalClicks: prev.totalClicks + reward,
      allTimeClicks: prev.allTimeClicks + reward,
    }));
    setHelperPigeon(null);
  }, [helperPigeon]);

  const claimEventSkin = () => {
    if (gs.ownedSkins.includes("golden_event")) return;
    setGs(g=>({...g, ownedSkins:[...g.ownedSkins,"golden_event"], currentSkin:"golden_event"}));
  };

  const allSkins = [...SKINS,...gs.customSkins];
  const activeSkin = allSkins.find(s=>s.id===gs.currentSkin)||SKINS[0];
  const isDevSkin = gs.currentSkin === "capybara";
  const isBoostActive = isDevSkin || !!(gs.activeBoost && Date.now()<gs.activeBoost.endsAt);
  const threshold = levelThreshold(gs.level);
  const progress = Math.min(100,(gs.xp/threshold)*100);
  const fmtTime = s=>`${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;
  const passiveIncome = Math.round((gs.talents.passive_inc||0)*(1+(gs.talents.gen_boost||0)*0.1)*(gs.prestigeBonus||1));
  const questList = gs.activeQuests?.map(qid=>QUEST_POOL.find(x=>x.id===qid)).filter(Boolean)||[];

  // ─────────────────────────────────────────────────────────────
  //  RENDER INTRO
  // ─────────────────────────────────────────────────────────────
  if (screen==="intro") return (
    <div style={{...S.wrap, background: eventActive ? "linear-gradient(160deg,#1a0e00,#3d2800,#1a1000)" : S.wrap.background}}>
      {eventActive && <EventBackground/>}
      <G/><link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;900&family=Bangers&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fi{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}`}</style>
      <div style={S.introCard}>
        <div style={S.introTitle}>🐦 PIGEON<br/>CLICKER</div>
        <div style={{color:"#aaa",fontSize:14,letterSpacing:1}}>Кликай. Прокачивай. Престижируй.</div>
        <div style={{animation:"fi 2.2s ease-in-out infinite"}}>{SKINS[0].svg(130)}</div>
        <input style={S.inp} placeholder="Твоё имя..." value={nameInput} onChange={e=>setNameInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&nameInput.trim()&&(setGs(g=>({...g,playerName:nameInput.trim()})),setScreen("game"))}
          maxLength={20}/>
        <button style={{...S.bigBtn,opacity:nameInput.trim()?1:0.5}}
          onClick={()=>{ if(nameInput.trim()){setGs(g=>({...g,playerName:nameInput.trim()}));setScreen("game");} }}>
          ИГРАТЬ!
        </button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  //  RENDER ADMIN
  // ─────────────────────────────────────────────────────────────
  if (screen==="admin") return (
    <div style={{...S.screen, background: eventActive ? "linear-gradient(160deg,#1a0e00,#3d2800,#1a1000)" : S.screen.background}}>
      {eventActive && <EventBackground/>}
      <G/><link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;900&family=Bangers&display=swap" rel="stylesheet"/>
      <div style={S.hdr}>
        <button style={S.back} onClick={()=>setScreen("game")}>← Назад</button>
        <span style={S.hdrTitle}>⚙️ АДМИНКА</span><span/>
      </div>
      {!adminLoggedIn ? (
        <div style={{...S.card,maxWidth:360,margin:"0 auto",width:"88%"}}>
          <div style={S.secT}>Введи пароль</div>
          <input style={S.inp} type="password" placeholder="Пароль..." value={adminPw} onChange={e=>setAdminPw(e.target.value)}/>
          {adminError&&<div style={{color:"#FF4444"}}>{adminError}</div>}
          <button style={S.bigBtn} onClick={()=>{ if(adminPw===ADMIN_PASSWORD){setAdminLoggedIn(true);setAdminError("");}else setAdminError("Неверный пароль!"); }}>Войти</button>
        </div>
      ):(
        <div style={{padding:"0 16px",maxWidth:500,margin:"0 auto",width:"100%"}}>
          {adminMsg&&<div style={S.adminMsg}>{adminMsg}</div>}

          <div style={S.card}>
            <div style={S.secT}>🎯 Очки и уровень</div>
            <div style={{color:"#aaa",fontSize:13}}>Игрок: <b style={{color:"#FFD700"}}>{gs.playerName}</b> — Lvl {gs.level}, {gs.totalClicks} очков</div>
            <div style={{display:"flex",gap:8}}>
              <input style={{...S.inp,flex:1}} type="number" placeholder="Очки..." value={adminClicks} onChange={e=>setAdminClicks(e.target.value)}/>
              <button style={{...S.bigBtn,width:"auto",padding:"12px 18px",fontSize:14}} onClick={()=>{
                const v=parseInt(adminClicks);
                if(!isNaN(v)&&v>=0){setGs(g=>({...g,totalClicks:v,clicks:v}));setAdminMsg("✅ Очки обновлены!");setTimeout(()=>setAdminMsg(""),2000);}
              }}>Сет</button>
            </div>
            <div style={{display:"flex",gap:8}}>
              <input style={{...S.inp,flex:1}} type="number" placeholder="Уровень (1–999)..." value={adminLevel} onChange={e=>setAdminLevel(e.target.value)}/>
              <button style={{...S.bigBtn,width:"auto",padding:"12px 18px",fontSize:14}} onClick={()=>{
                const v=parseInt(adminLevel);
                if(!isNaN(v)&&v>=1&&v<=999){
                  const tp = Math.floor(v/2);
                  setGs(g=>({...g,level:v,xp:0,talentPoints:g.talentPoints+tp}));
                  setAdminMsg(`✅ Уровень ${v} + ${tp} тал. очков!`);setTimeout(()=>setAdminMsg(""),2500);
                }
              }}>Сет</button>
            </div>
            <button style={{...S.bigBtn,background:"rgba(100,255,100,0.15)",color:"#88FF88",border:"1px solid #44AA44"}} onClick={()=>{
              setGs(g=>({...g,talentPoints:g.talentPoints+10}));
              setAdminMsg("✅ +10 очков талантов!");setTimeout(()=>setAdminMsg(""),2000);
            }}>+10 очков талантов</button>
          </div>

          <div style={{...S.card, background:"rgba(180,120,0,0.12)", border:"2px solid #C8A87866"}}>
            <div style={S.secT}>💻 Подарок разработчика</div>
            <div style={{color:"#aaa",fontSize:13}}>Dev Голубь — эксклюзивный скин. Перма x10 + частые джуны.</div>
            <div style={{display:"flex",alignItems:"center",gap:14,marginTop:4}}>
              <div style={{flex:1}}>
                {SKINS.find(s=>s.id==="capybara")?.svg(60)}
              </div>
              <div style={{flex:2}}>
                <div style={{color:"#C8A878",fontWeight:900,fontSize:14}}>Капибара 🎁</div>
                <div style={{color:"#888",fontSize:12,marginTop:2}}>
                  {gs.ownedSkins.includes("capybara") ? "✅ Уже выдана игроку" : "❌ Не выдана"}
                </div>
              </div>
              <button style={{
                ...S.bigBtn, width:"auto", padding:"10px 18px",
                background: gs.ownedSkins.includes("capybara")
                  ? "rgba(255,0,0,0.2)" : "linear-gradient(135deg,#C8A878,#FFD700)",
                color: gs.ownedSkins.includes("capybara") ? "#FF8888" : "#1a0a00",
                border: gs.ownedSkins.includes("capybara") ? "1px solid #FF444466" : "none",
                fontSize:13,
              }} onClick={()=>{
                if (gs.ownedSkins.includes("capybara")) {
                  setGs(g=>({...g, ownedSkins:g.ownedSkins.filter(s=>s!=="capybara"), currentSkin:g.currentSkin==="capybara"?"default":g.currentSkin}));
                  setAdminMsg("🦫 Скин отозван");
                } else {
                  setGs(g=>({...g, ownedSkins:[...g.ownedSkins,"capybara"], currentSkin:"capybara"}));
                  setAdminMsg("🦫 Капибара выдана!");
                }
                setTimeout(()=>setAdminMsg(""),2500);
              }}>
                {gs.ownedSkins.includes("capybara") ? "Отозвать" : "🎁 Выдать"}
              </button>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.secT}>🖼️ Загрузить скин</div>
            <input style={S.inp} placeholder="Название..." value={skinUpName} onChange={e=>setSkinUpName(e.target.value)}/>
            <input style={S.inp} type="number" placeholder="Цена..." value={skinUpPrice} onChange={e=>setSkinUpPrice(e.target.value)}/>
            <label style={S.uploadLbl}>
              {skinUpImg?"✅ Файл выбран":"📁 Выбрать изображение"}
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                const f=e.target.files[0];if(!f)return;
                const r=new FileReader();r.onload=ev=>setSkinUpImg(ev.target.result);r.readAsDataURL(f);
              }}/>
            </label>
            <button style={S.bigBtn} onClick={()=>{
              if(!skinUpName.trim()||!skinUpImg){setAdminMsg("❌ Укажи название и файл");setTimeout(()=>setAdminMsg(""),2000);return;}
              const id="custom_"+Date.now(),imgSrc=skinUpImg;
              setGs(g=>({...g,customSkins:[...g.customSkins,{id,name:skinUpName.trim(),price:parseInt(skinUpPrice)||500,emoji:"🖼️",color:"#888",isCustom:true,imgSrc,svg:(sz=120)=><img src={imgSrc} width={sz} height={sz} style={{objectFit:"contain",borderRadius:8}}/>}]}));
              setSkinUpName("");setSkinUpImg(null);setSkinUpPrice("500");
              setAdminMsg("✅ Скин добавлен!");setTimeout(()=>setAdminMsg(""),2000);
            }}>Добавить</button>
          </div>


        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  //  RENDER SHOP
  // ─────────────────────────────────────────────────────────────
  if (screen==="shop") return (
    <div style={{...S.screen, background: eventActive ? "linear-gradient(160deg,#1a0e00,#3d2800,#1a1000)" : S.screen.background}}>
      {eventActive && <EventBackground/>}
      <G/><link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;900&family=Bangers&display=swap" rel="stylesheet"/>
      <div style={S.hdr}>
        <button style={S.back} onClick={()=>setScreen("game")}>← Назад</button>
        <span style={S.hdrTitle}>🛒 МАГАЗИН</span>
        <span style={{color:"#FFD700",fontWeight:900}}>💰 {gs.totalClicks.toLocaleString()}</span>
      </div>
      <div style={{padding:"0 16px",maxWidth:540,width:"100%",boxSizing:"border-box",margin:"0 auto"}}>
        <div style={{...S.secT,fontSize:22,marginBottom:10}}>⚡ БУСТЫ</div>
        {SHOP_BOOSTS.map(boost=>{
          const act=isBoostActive&&gs.activeBoost?.multiplier>=boost.multiplier;
          const canBuy=!act&&gs.totalClicks>=boost.price;
          return (
            <div key={boost.id} style={{background:act?"rgba(255,69,0,0.18)":"rgba(255,255,255,0.07)",border:act?"2px solid #FF4500":"2px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"16px 18px",display:"flex",alignItems:"center",gap:14,marginBottom:14,boxShadow:act?"0 0 24px #FF450055":"none"}}>
              <span style={{fontSize:36}}>{boost.emoji}</span>
              <div style={{flex:1}}>
                <div style={{color:"white",fontWeight:900,fontSize:16}}>{boost.name}</div>
                <div style={{color:"#aaa",fontSize:12}}>{boost.desc}</div>
                {act&&<div style={{color:"#FF6600",fontWeight:900,fontSize:12,marginTop:2}}>⏱ {fmtTime(boostTimeLeft)}</div>}
              </div>
              {act?<div style={{color:"#FF4500",fontWeight:900}}>АКТИВЕН</div>
                :<button style={{...S.shopBtn,width:"auto",padding:"10px 16px",opacity:canBuy?1:0.4,background:canBuy?"linear-gradient(135deg,#FF4500,#FF8C00)":"#555"}} onClick={()=>buyBoost(boost)}>💰 {boost.price}</button>}
            </div>
          );
        })}
        <div style={{...S.secT,fontSize:22,marginBottom:10,marginTop:8}}>🎨 СКИНЫ</div>
      </div>
      <div style={S.shopGrid}>
        {allSkins.map(skin=>{
          const owned=gs.ownedSkins.includes(skin.id),active=gs.currentSkin===skin.id,canBuy=!owned&&gs.totalClicks>=skin.price;
          const isEventSkin = skin.isEvent;
          const isGift = skin.isDeveloperGift;
          const eventBorder = isGift
            ? (active?"2px solid #C8A878":owned?"2px solid #C8A878":"2px solid #55443366")
            : isEventSkin
              ? (active?"2px solid #FFD700":owned?"2px solid #FFD700":"2px solid #B8860B")
              : (active?"2px solid #FFD700":owned?"2px solid #4CAF50":"2px solid rgba(255,255,255,0.1)");
          const eventGlow = isGift
            ? (owned ? "0 0 16px #C8A87866" : "none")
            : isEventSkin
              ? (active?"0 0 24px #FFD700,0 0 48px #FF8800":"0 0 12px #FFD70055")
              : (active?"0 0 20px #FFD70066":"none");
          return (
            <div key={skin.id} style={{...S.skinCard,border:eventBorder,boxShadow:eventGlow,background:isEventSkin?"rgba(180,120,0,0.18)":undefined,position:"relative"}}>
              {isGift&&<div style={{position:"absolute",top:-8,left:"50%",transform:"translateX(-50%)",background:"linear-gradient(90deg,#6B4C2A,#C8A878,#6B4C2A)",color:"#1a0a00",fontSize:9,fontWeight:900,padding:"2px 10px",borderRadius:8,whiteSpace:"nowrap",letterSpacing:1}}>🎁 ПОДАРОК</div>}
              {isEventSkin&&<div style={{position:"absolute",top:-8,left:"50%",transform:"translateX(-50%)",background:"linear-gradient(90deg,#B8860B,#FFD700,#B8860B)",color:"#1a0a00",fontSize:9,fontWeight:900,padding:"2px 10px",borderRadius:8,whiteSpace:"nowrap",letterSpacing:1}}>★ ИВЕНТ</div>}
              <div style={{transform:active?"scale(1.07)":"scale(1)",transition:"transform 0.2s",marginTop:isEventSkin?8:0}}>
                {skin.isCustom?<img src={skin.imgSrc} width={78} height={78} style={{objectFit:"contain",borderRadius:8}}/>:skin.svg(78)}
              </div>
              <div style={{color:isEventSkin?"#FFD700":"white",fontWeight:700,fontSize:12,textAlign:"center"}}>{skin.name}</div>
              <div style={{fontSize:18}}>{skin.emoji}</div>
              {owned
                ? <button style={{...S.shopBtn,background:active?"#FFD700":"#4CAF50",color:"#000"}} onClick={()=>setGs(g=>({...g,currentSkin:skin.id}))}>{active?"✓ Активен":"Надеть"}</button>
                : isGift
                  ? <div style={{fontSize:10,color:"#C8A878",textAlign:"center",padding:"4px 0",fontWeight:700}}>🔒 Подарок<br/>разработчика</div>
                  : isEventSkin
                  ? eventActive
                    ? <button style={{...S.shopBtn,background:"linear-gradient(90deg,#B8860B,#FFD700)",color:"#1a0a00",fontWeight:900}} onClick={claimEventSkin}>🎁 ЗАБРАТЬ</button>
                    : <div style={{fontSize:10,color:"#888",textAlign:"center"}}>Ивент завершён</div>
                  : <button style={{...S.shopBtn,opacity:canBuy?1:0.4,background:canBuy?"#FF8C00":"#555"}} onClick={()=>buySkin(skin)}>💰 {skin.price.toLocaleString()}</button>
              }
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  //  RENDER TALENTS
  // ─────────────────────────────────────────────────────────────
  if (screen==="talents") return (
    <div style={{...S.screen, background: eventActive ? "linear-gradient(160deg,#1a0e00,#3d2800,#1a1000)" : S.screen.background}}>
      {eventActive && <EventBackground/>}
      <G/><link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;900&family=Bangers&display=swap" rel="stylesheet"/>
      <div style={S.hdr}>
        <button style={S.back} onClick={()=>setScreen("game")}>← Назад</button>
        <span style={S.hdrTitle}>🌟 ТАЛАНТЫ</span>
        <span style={{color:"#CC44FF",fontWeight:900}}>⭐ {gs.talentPoints}</span>
      </div>
      <div style={{padding:"0 14px",maxWidth:500,margin:"0 auto",width:"100%"}}>
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          {Object.entries(TALENT_TREES).map(([key,tree])=>(
            <button key={key} style={{...S.tabBtn,background:talentTab===key?tree.color:"rgba(255,255,255,0.07)",color:talentTab===key?"#000":"#aaa",border:talentTab===key?`2px solid ${tree.color}`:"2px solid transparent"}} onClick={()=>setTalentTab(key)}>
              {tree.icon} {tree.name}
            </button>
          ))}
        </div>
        {Object.entries(TALENT_TREES).map(([key,tree])=>talentTab!==key?null:(
          <div key={key}>
            {tree.talents.map(t=>{
              const cur=gs.talents[t.id]||0, maxed=cur>=t.max, canBuy=gs.talentPoints>0&&!maxed;
              return (
                <div key={t.id} style={{...S.card,flexDirection:"row",alignItems:"center",gap:14,padding:"14px 16px",marginBottom:10}}>
                  <div style={{flex:1}}>
                    <div style={{color:"white",fontWeight:900,fontSize:15}}>{t.name}</div>
                    <div style={{color:"#aaa",fontSize:12,marginTop:2}}>{t.desc}</div>
                    <div style={{display:"flex",gap:4,marginTop:6}}>
                      {Array.from({length:t.max}).map((_,i)=>(
                        <div key={i} style={{width:14,height:14,borderRadius:4,background:i<cur?tree.color:"rgba(255,255,255,0.15)"}}/>
                      ))}
                    </div>
                  </div>
                  <button style={{background:canBuy?tree.color:"rgba(255,255,255,0.1)",border:"none",borderRadius:14,padding:"10px 16px",color:canBuy?"#000":"#555",fontWeight:900,fontFamily:"'Nunito',sans-serif",fontSize:13,cursor:canBuy?"pointer":"default",opacity:maxed?0.5:1,minWidth:70}}
                    onClick={()=>investTalent(t.id)}>
                    {maxed?"МАКС":"+ 1 ⭐"}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
        {gs.level>=20&&(
          <div style={{...S.card,background:"rgba(180,0,255,0.15)",border:"2px solid #CC44FF",textAlign:"center",gap:8}}>
            <div style={{fontFamily:"'Bangers',cursive",fontSize:24,color:"#CC44FF",letterSpacing:2}}>♾️ ПРЕСТИЖ ДОСТУПЕН</div>
            <div style={{color:"#ccc",fontSize:13}}>Сбросить прогресс, но получить постоянный +25% ко всем очкам.<br/>Текущий бонус: ×{gs.prestigeBonus.toFixed(2)}</div>
            <button style={{...S.bigBtn,background:"linear-gradient(135deg,#6600CC,#CC44FF)",color:"white"}} onClick={handlePrestige}>♾️ ПРЕСТИЖ #{gs.prestigeCount+1}</button>
          </div>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  //  RENDER QUESTS
  // ─────────────────────────────────────────────────────────────
  if (screen==="quests") return (
    <div style={{...S.screen, background: eventActive ? "linear-gradient(160deg,#1a0e00,#3d2800,#1a1000)" : S.screen.background}}>
      {eventActive && <EventBackground/>}
      <G/><link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;900&family=Bangers&display=swap" rel="stylesheet"/>
      <div style={S.hdr}>
        <button style={S.back} onClick={()=>setScreen("game")}>← Назад</button>
        <span style={S.hdrTitle}>📋 ЗАДАНИЯ</span><span/>
      </div>
      <div style={{padding:"0 16px",maxWidth:500,margin:"0 auto",width:"100%"}}>
        <div style={{...S.secT,marginBottom:12}}>Активные задания</div>
        {questList.length===0&&<div style={{color:"#888",textAlign:"center",padding:24}}>Все задания выполнены!</div>}
        {questList.map(q=>{
          let cur=0,goal=q.goal;
          if(q.type==="clicks") cur=gs.allTimeClicks;
          if(q.type==="crits") cur=gs.questProgress.crits||0;
          if(q.type==="level") cur=gs.level;
          if(q.type==="combo") cur=parseFloat(gs.comboMult.toFixed(2));
          if(q.type==="passive") cur=gs.questProgress.passive||0;
          if(q.type==="streak") cur=gs.dailyStreak||0;
          const pct=Math.min(100,(cur/goal)*100);
          return (
            <div key={q.id} style={{...S.card,gap:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{color:"white",fontWeight:700,fontSize:14}}>{q.desc}</div>
                <div style={{color:"#FFD700",fontWeight:900,fontSize:13}}>+{q.reward} 💰</div>
              </div>
              <div style={{height:6,background:"rgba(255,255,255,0.1)",borderRadius:6,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#FF8C00,#FFD700)",borderRadius:6,transition:"width 0.3s"}}/>
              </div>
              <div style={{color:"#aaa",fontSize:11}}>{q.type==="combo"?cur.toFixed(2):Math.min(cur,goal)} / {q.type==="combo"?goal.toFixed(2):goal}</div>
            </div>
          );
        })}
        <div style={{...S.secT,marginBottom:12,marginTop:16}}>Выполнено ({gs.completedQuests.length})</div>
        {gs.completedQuests.map((qid,i)=>{
          const q=QUEST_POOL.find(x=>x.id===qid);
          return q?<div key={i} style={{...S.card,flexDirection:"row",gap:12,alignItems:"center",opacity:0.6,padding:"10px 14px"}}>
            <span style={{fontSize:18}}>✅</span>
            <span style={{color:"#aaa",fontSize:13,flex:1}}>{q.desc}</span>
            <span style={{color:"#FFD700",fontSize:12}}>+{q.reward}</span>
          </div>:null;
        })}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  //  RENDER GAME
  // ─────────────────────────────────────────────────────────────
  const pigeonFilter = visualEffect
    ? "drop-shadow(0 0 20px #FFD700) drop-shadow(0 0 40px #FF8800)"
    : gs.currentSkin==="golden_event"
      ? "drop-shadow(0 0 18px #FFD700) drop-shadow(0 0 36px #FF8800)"
    : isDevSkin
      ? "drop-shadow(0 0 16px #00FFCC) drop-shadow(0 0 32px #00CC88)"
    : isBoostActive
      ? "drop-shadow(0 8px 28px rgba(255,69,0,.8))"
      : "drop-shadow(0 8px 24px rgba(0,0,0,0.4))";

  return (
    <div style={{...S.game, background: eventActive ? "linear-gradient(160deg,#1a0e00,#3d2800,#1a1000)" : S.game.background}}>
      {eventActive && <EventBackground/>}
      {eventActive && screen==="game" && (
        <EventBanner onClaim={claimEventSkin} claimed={gs.ownedSkins.includes("golden_event")}/>
      )}
      <G/>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;900&family=Bangers&display=swap" rel="stylesheet"/>
      <style>{`
        @keyframes pb{0%,100%{transform:scale(1) rotate(0deg)}50%{transform:scale(0.85) rotate(-5deg)}}
        @keyframes fg{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        @keyframes glow{0%,100%{filter:drop-shadow(0 0 20px #FFD700) drop-shadow(0 0 40px #FF8800)}50%{filter:drop-shadow(0 0 30px #FFD700) drop-shadow(0 0 60px #FF4400)}}
        @keyframes bglow{0%,100%{filter:drop-shadow(0 8px 28px rgba(255,69,0,.8))}50%{filter:drop-shadow(0 8px 52px rgba(255,140,0,1))}}
        @keyframes devglow{0%,100%{filter:drop-shadow(0 0 16px #00FFCC) drop-shadow(0 0 32px #00CC88)}50%{filter:drop-shadow(0 0 28px #00FFCC) drop-shadow(0 0 56px #00FF88)}}
        @keyframes si{from{opacity:0;transform:scale(.7)}to{opacity:1;transform:scale(1)}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes crit{0%{transform:scale(1)}30%{transform:scale(1.15)}100%{transform:scale(1)}}
        body.game-screen { overflow: hidden !important; height: 100%; }
      `}</style>

      {showLevelUp&&<LevelUpOverlay level={levelUpNum} onDone={()=>setShowLevelUp(false)}/>}
      {randomEvent&&<RandomEventToast event={randomEvent} onDone={()=>setRandomEvent(null)}/>}
      {helperPigeon&&<HelperPigeon pigeon={helperPigeon} onCollect={collectHelperPigeon}/>}
      {showDaily&&pendingDaily&&<DailyRewardModal streak={pendingDaily.streak} reward={pendingDaily.reward} onClaim={claimDaily}/>}
      {particles.map(p=><Particle key={p.id} x={p.x} y={p.y} text={p.text} color={p.color} onDone={()=>removeParticle(p.id)}/>)}

      {/* TOP BAR */}
      <div style={S.topBar}>
        <div style={{display:"flex",gap:6,alignItems:"center",color:"white"}}>
          <span style={{fontSize:16}}>🐦</span>
          <span style={{fontWeight:900,fontSize:14}}>{gs.playerName}</span>
          {gs.prestigeCount>0&&<span style={{fontSize:11,color:"#CC44FF",fontWeight:900}}>♾️×{gs.prestigeCount}</span>}
        </div>
        <div style={{display:"flex",gap:5,flexWrap:"wrap",justifyContent:"flex-end"}}>
          {gs.dailyStreak>0&&<span style={{...S.chip,background:"rgba(255,140,0,0.2)",color:"#FF8C00",border:"1px solid #FF8C0044"}}>🔥{gs.dailyStreak}д</span>}
          <span style={S.chip}>Lvl <b>{gs.level}</b></span>
          <span style={S.chip}>💰 <b>{gs.totalClicks.toLocaleString()}</b></span>
          {passiveIncome>0&&<span style={{...S.chip,background:"rgba(0,191,255,0.15)",color:"#00BFFF",border:"1px solid #00BFFF33"}}>+{passiveIncome}/с</span>}
        </div>
      </div>

      {/* PROGRESS BAR with milestones */}
      <div style={{width:"100%",padding:"8px 16px 0",boxSizing:"border-box"}}>
        <div style={{display:"flex",justifyContent:"space-between",color:"#aaa",fontSize:11,marginBottom:3}}>
          <span>Lvl {gs.level} → {gs.level+1}</span>
          <span>{gs.xp.toLocaleString()} / {threshold.toLocaleString()} XP</span>
        </div>
        <div style={{position:"relative",height:10,background:"rgba(255,255,255,0.08)",borderRadius:10,overflow:"hidden"}}>
          <div style={{height:"100%",borderRadius:10,width:`${progress}%`,background:isBoostActive?"linear-gradient(90deg,#FF4500,#FF8C00)":milestoneX2Active?"linear-gradient(90deg,#8800FF,#CC44FF)":visualEffect?"linear-gradient(90deg,#FFD700,#FFEE44)":"linear-gradient(90deg,#FF8C00,#FFD700)",transition:"width 0.25s"}}/>
        </div>
      </div>

      {/* COMBO */}
      {gs.comboMult>1.05&&(
        <div style={{marginTop:6,fontSize:12,color:"#FF8800",fontWeight:900,letterSpacing:1,animation:"si 0.2s"}}>
          🔥 КОМБО ×{gs.comboMult.toFixed(2)}
        </div>
      )}

      {/* PIGEON */}
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:0}}>
        <div style={{cursor:"pointer",userSelect:"none",
          animation:pigeonAnim?"pb 0.15s ease":visualEffect?"fg 1.5s ease-in-out infinite,glow 1s ease-in-out infinite":isDevSkin?"fg 1.4s ease-in-out infinite,devglow 1s ease-in-out infinite":isBoostActive?"fg 1.4s ease-in-out infinite,bglow 0.9s ease-in-out infinite":"fg 3s ease-in-out infinite",
          filter:pigeonFilter,transition:"filter 0.3s"
        }} onClick={handleClick}>
          {activeSkin.isCustom?<img src={activeSkin.imgSrc} width={148} height={148} style={{objectFit:"contain",borderRadius:12}}/>:activeSkin.svg(148)}
        </div>
        <div style={{fontSize:11,marginTop:4,letterSpacing:1,color:"rgba(255,255,255,0.2)"}}>
          Тапай меня!
        </div>
      </div>

      {/* ACTIVE QUESTS MINI */}
      {(()=>{
        const incomplete = questList.filter(q=>{
          let cur=0;
          if(q.type==="clicks") cur=gs.allTimeClicks;
          if(q.type==="crits") cur=gs.questProgress.crits||0;
          if(q.type==="level") cur=gs.level;
          if(q.type==="combo") cur=gs.comboMult;
          if(q.type==="passive") cur=gs.questProgress.passive||0;
          if(q.type==="streak") cur=gs.dailyStreak||0;
          return cur < q.goal;
        });
        if (!incomplete.length) return null;
        const q = incomplete[0];
        let cur=0;
        if(q.type==="clicks") cur=gs.allTimeClicks;
        if(q.type==="crits") cur=gs.questProgress.crits||0;
        if(q.type==="level") cur=gs.level;
        if(q.type==="combo") cur=gs.comboMult;
        if(q.type==="passive") cur=gs.questProgress.passive||0;
        if(q.type==="streak") cur=gs.dailyStreak||0;
        const pct=Math.min(100,(cur/q.goal)*100);
        return (
          <div style={{width:"100%",padding:"0 16px 4px",boxSizing:"border-box",maxWidth:460,margin:"0 auto"}}>
            <div style={{background:"rgba(255,255,255,0.05)",borderRadius:14,padding:"8px 14px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:14}}>📋</span>
              <div style={{flex:1}}>
                <div style={{color:"#ccc",fontSize:11,marginBottom:2}}>{q.desc}</div>
                <div style={{height:4,background:"rgba(255,255,255,0.1)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#FF8C00,#FFD700)",transition:"width 0.3s"}}/>
                </div>
              </div>
              <span style={{color:"#FFD700",fontSize:11,fontWeight:900}}>+{q.reward}</span>
            </div>
          </div>
        );
      })()}

      {/* BOTTOM NAV */}
      <div style={S.nav}>
        <button style={S.navBtn} onClick={()=>setScreen("shop")}><span style={{fontSize:20}}>🛒</span><span>Магазин</span></button>
        <button style={{...S.navBtn,position:"relative"}} onClick={()=>setScreen("talents")}>
          <span style={{fontSize:20}}>🌟</span><span>Таланты</span>
          {gs.talentPoints>0&&<span style={{position:"absolute",top:-4,right:8,background:"#CC44FF",color:"white",borderRadius:10,fontSize:10,fontWeight:900,padding:"1px 6px"}}>{gs.talentPoints}</span>}
        </button>
        <button style={S.navBtn} onClick={()=>setScreen("quests")}><span style={{fontSize:20}}>📋</span><span>Задания</span></button>
        <button style={S.navBtn} onClick={()=>setScreen("admin")}><span style={{fontSize:20}}>⚙️</span><span>Админ</span></button>
      </div>
    </div>
  );
}

// Global background styles component
function G() {
  return <style>{`
    * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
    body { margin: 0; font-family: 'Nunito', sans-serif; }
    button, div, span { -webkit-tap-highlight-color: transparent; outline: none; }
  `}</style>;
}

// ══════════════════════════════════════════════════════════════
//  STYLES
// ══════════════════════════════════════════════════════════════
const BG = "linear-gradient(160deg,#0f0c29,#302b63,#24243e)";
const FONT = "'Nunito',sans-serif";
const S = {
  wrap:   { minHeight:"100vh",background:BG,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FONT,userSelect:"none" },
  screen: { minHeight:"100vh",background:BG,fontFamily:FONT,paddingBottom:40,display:"flex",flexDirection:"column",alignItems:"center",width:"100%",userSelect:"none",overflowY:"auto" },
  game:   { height:"100vh",maxHeight:"100vh",background:BG,display:"flex",flexDirection:"column",alignItems:"center",fontFamily:FONT,paddingBottom:68,overflowX:"hidden",overflowY:"hidden",userSelect:"none" },
  introCard: { background:"rgba(255,255,255,0.07)",backdropFilter:"blur(20px)",borderRadius:32,padding:"36px 28px",maxWidth:360,width:"90%",display:"flex",flexDirection:"column",alignItems:"center",gap:14,border:"1px solid rgba(255,255,255,0.15)",boxShadow:"0 24px 64px rgba(0,0,0,0.5)" },
  introTitle: { fontFamily:"'Bangers',cursive",fontSize:54,color:"#FFD700",textAlign:"center",lineHeight:1,letterSpacing:3,textShadow:"0 4px 20px #FF880088" },
  inp: { width:"100%",padding:"12px 16px",borderRadius:14,border:"2px solid rgba(255,215,0,0.4)",background:"rgba(255,255,255,0.1)",color:"white",fontSize:16,fontFamily:FONT,outline:"none",boxSizing:"border-box",textAlign:"center" },
  bigBtn: { width:"100%",padding:"13px",borderRadius:14,background:"linear-gradient(135deg,#FF8C00,#FFD700)",color:"#1a1a1a",fontWeight:900,fontSize:17,border:"none",cursor:"pointer",fontFamily:FONT,letterSpacing:1 },
  topBar: { width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",boxSizing:"border-box",background:"rgba(0,0,0,0.35)",backdropFilter:"blur(10px)" },
  chip: { background:"rgba(255,215,0,0.15)",color:"#FFD700",padding:"3px 9px",borderRadius:20,fontSize:12,border:"1px solid #FFD70033" },
  nav: { position:"fixed",bottom:0,left:0,right:0,display:"flex",justifyContent:"space-around",alignItems:"center",background:"rgba(10,8,30,0.97)",backdropFilter:"blur(20px)",padding:"9px 0 13px",borderTop:"1px solid rgba(255,255,255,0.1)",zIndex:100 },
  navBtn: { display:"flex",flexDirection:"column",alignItems:"center",gap:1,background:"none",border:"none",color:"#888",cursor:"pointer",fontSize:11,fontFamily:FONT,padding:"0 8px",position:"relative" },
  hdr: { width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px",boxSizing:"border-box",background:"rgba(0,0,0,0.3)",backdropFilter:"blur(10px)",marginBottom:14,flexShrink:0 },
  back: { background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",color:"white",padding:"8px 13px",borderRadius:12,cursor:"pointer",fontFamily:FONT,fontSize:13 },
  hdrTitle: { fontFamily:"'Bangers',cursive",fontSize:26,color:"#FFD700",letterSpacing:2 },
  shopGrid: { display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,padding:"0 14px",maxWidth:540,width:"100%",boxSizing:"border-box",margin:"0 auto" },
  skinCard: { background:"rgba(255,255,255,0.07)",borderRadius:18,padding:"14px 10px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"space-between",gap:6,transition:"transform 0.15s",minHeight:180 },
  shopBtn: { width:"100%",padding:"7px",borderRadius:11,border:"none",cursor:"pointer",fontWeight:900,fontFamily:FONT,fontSize:12,color:"white",transition:"opacity 0.15s" },
  lbRow: { display:"flex",gap:10,alignItems:"center",padding:"11px 14px",borderRadius:13,marginBottom:7,color:"white",fontSize:14 },
  card: { background:"rgba(255,255,255,0.07)",borderRadius:18,padding:"16px",marginBottom:14,display:"flex",flexDirection:"column",gap:8,width:"100%",boxSizing:"border-box" },
  secT: { fontFamily:"'Bangers',cursive",fontSize:19,color:"#FFD700",letterSpacing:1,marginBottom:3 },
  adminMsg: { background:"rgba(255,215,0,0.2)",border:"1px solid #FFD70066",color:"#FFD700",padding:"9px 14px",borderRadius:11,marginBottom:11,textAlign:"center",fontWeight:700,animation:"si 0.3s" },
  uploadLbl: { display:"block",padding:"11px",borderRadius:14,cursor:"pointer",border:"2px dashed rgba(255,215,0,0.4)",color:"#FFD700",textAlign:"center",fontWeight:700,fontSize:13 },
  smlBtn: { background:"rgba(255,0,0,0.2)",border:"1px solid rgba(255,0,0,0.4)",color:"#FF6666",borderRadius:7,padding:"3px 8px",cursor:"pointer",fontSize:11,fontFamily:FONT },
  tabBtn: { padding:"8px 14px",borderRadius:12,border:"none",cursor:"pointer",fontWeight:700,fontFamily:FONT,fontSize:12,transition:"all 0.15s" },
};
